import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { jwtDecode } from "jwt-decode";
import CreatableSelect, { useCreatable } from "react-select/creatable";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import ImageModule from "docxtemplater-image-module-free";
import { PDFDocument } from "pdf-lib";
import { saveAs } from "file-saver";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { TrashIcon } from "@heroicons/react/20/solid";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import AlertDialog from "./AlertDialog";
const API = "https://erp-xe9u.onrender.com/api";
// Custom styles for react-select (unchanged)
const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "38px",
    height: "24px",
    fontSize: "12px",
    color: "#e5e7eb",
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    margin: 0,
    padding: 0,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 4px",
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: "12px",
    color: "#e5e7eb",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    padding: "0",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "2px",
    color: "#9ca3af",
    "&:hover": { color: "#e5e7eb" },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#6b7280" },
    fontSize: "12px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    zIndex: 9999,
    maxHeight: "200px",
    overflowY: "auto",
    position: "absolute",
    width: "100%",
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: "200px",
    overflowY: "auto",
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
};

// Confirmation Modal (unchanged)
const ConfirmationModal = ({ open, onClose, onConfirm }) => {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-10">
      <DialogBackdrop className="fixed inset-0 bg-gray-600/75 transition-opacity" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative transform overflow-hidden rounded-lg bg-gray-700 px-4 pb-4 pt-5 shadow-xl transition-all sm:max-w-lg sm:p-6 border border-gray-600">
            <div className="absolute right-0 top-0 pr-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md text-gray-400 hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <XMarkIcon className="size-5" />
              </button>
            </div>
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-600 sm:mx-0">
                <ExclamationTriangleIcon className="size-6 text-red-400" />
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle
                  as="h3"
                  className="text-lg font-semibold text-orange-400"
                >
                  Confirm Rejection
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-200">
                    Changing the status from Approved to Rejected will remove
                    only items not linked to a Purchase Order (PO) or Work Order
                    (WO). Items linked to POs or WOs will remain. Are you sure
                    you want to proceed?
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-4 sm:flex sm:flex-row-reverse sm:gap-3">
              <button
                type="button"
                onClick={onConfirm}
                className="inline-flex w-full justify-center rounded-md bg-red-400 px-4 py-2 text-sm font-semibold text-gray-200 shadow-sm hover:bg-red-500 sm:w-auto transition-colors duration-200"
              >
                Reject
              </button>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex w-full justify-center rounded-md bg-gray-600 px-4 py-2 text-sm font-semibold text-gray-200 shadow-sm ring-1 ring-gray-500 hover:bg-gray-500 sm:w-auto transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default function EditChangeOrderForm() {
  const { id, changeOrderId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: "",
    termsAndConditions: "",
    approvalStatus: "Pending",
    overheadPercentage: 0,
    changesToSchedule: 0,
    timeAndMaterialId: null,
  });
  const [emailForm, setEmailForm] = useState({
    to: "",
    subject: "",
    body: "",
    signature: "",
  });
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [laborItems, setLaborItems] = useState([]);
  const [surchargeItems, setSurchargeItems] = useState([]);

  const [uploadedItems, setUploadedItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [project, setProject] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [newLaborItem, setNewLaborItem] = useState({
    labourTitle: "",
    quantity: 0,
    unitCost: 0,
    unitPrice: 0,
    uom: "",
    bidArea: "",
    costCode: "",
    customer: true,
    budget: true,
  });
  const [newSurchargeItem, setNewSurchargeItem] = useState({
    title: "",
    quantity: 0,
    unitCost: 0,
    unitPrice: 0,
    costCode: "",
    customer: true,
    budget: true,
  });
  // New state for material item
  const [newMaterialItem, setNewMaterialItem] = useState({
    title: "",
    quantity: 0,
    unitCost: 0,
    price: 0,
    vendor: null,
    costCode: "",
    bidArea: "",
    itemCode: "",
    itemDescription: "",
    size: "",
    container: "",
    customer: true,
    budget: true,
  });
  const [summary, setSummary] = useState({
    description: "",
    qtyUnit: "",
    totalPriceForCustomer: 0,
    totalCost: 0,
    billable: true,
    markup: 0,
    revision: 0, // Add revision to summary
  });
  const [savedChangeOrder, setSavedChangeOrder] = useState(null);
  const [showBudgetUpload, setShowBudgetUpload] = useState(false);
  const [budgetFile, setBudgetFile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  const [bidAreaOptions, setBidAreaOptions] = useState([]);
  const [availableLabourItems, setAvailableLabourItems] = useState([]);
  const [availableMaterialItems, setAvailableMaterialItems] = useState([]); // New state for material items
  const [selectedLabourOption, setSelectedLabourOption] = useState(null);
  const [selectedMaterialOption, setSelectedMaterialOption] = useState(null); // New state for selected material
  const [timeAndMaterialData, setTimeAndMaterialData] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [pdfBuffers, setPdfBuffers] = useState({ co: null, tm: null });
  const [includeTM, setIncludeTM] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
  });
  // New state for toggling input visibility
  const [showLaborInput, setShowLaborInput] = useState(false);
  const [showSurchargeInput, setShowSurchargeInput] = useState(false);
  const [showMaterialInput, setShowMaterialInput] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("token");
        const requests = [
          axios.get(`https://erp-xe9u.onrender.com/api/projects/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get("https://erp-xe9u.onrender.com/api/accounts/vendors", {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get("https://erp-xe9u.onrender.com/api/company-profile", {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(
            `https://erp-xe9u.onrender.com/api/changeOrders/${changeOrderId}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get("https://erp-xe9u.onrender.com/api/costCodes", {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ];
        const [
          projectResponse,
          vendorsResponse,
          companyProfileResponse,
          changeOrderResponse,
          costCodesResponse,
        ] = await Promise.all(requests);
        setProject(projectResponse.data);
        setVendors(vendorsResponse.data);
        setCostCodes(costCodesResponse.data);

        // Process labor items from contractLabourItems
        const contractLaborOptions = projectResponse.data.contractLabourItems
          .filter((item) => item && item.labourItem)
          .map((item) => {
            let costCode = item.costCode || null;
            const sectionNumber = item.sectionNumber || "";
            if (!costCode && sectionNumber) {
              const sectionNumberLower = sectionNumber.trim().toLowerCase();
              const matchingCostCode = costCodesResponse.data.find((cc) =>
                cc.code?.trim().toLowerCase().includes(sectionNumberLower),
              );
              costCode = matchingCostCode ? matchingCostCode._id : null;
              if (!costCode) {
                console.warn(
                  `No cost code found for contract labor sectionNumber: ${sectionNumber}`,
                );
              }
            }
            return {
              value: item._id,
              label: `${item.labourItem?.title || "Unnamed"} (Rate: $${(
                item.unitRate || 0
              ).toFixed(2)} / Cost: $${(item.unitCost || 0).toFixed(2)} / ${
                item.uom || "N/A"
              })`,
              unitRate: item.unitRate || 0,
              unitCost: item.unitCost || 0,
              uom: item.uom || "",
              itemCode: item.itemCode || "",
              itemDescription: item.labourItem?.title || "Unnamed",
              bidArea: item.bidArea || "",
              costCode,
              sectionNumber,
              source: "contract", // Add source to distinguish contract items
            };
          });

        // Process labor items from budgets (category "L")
        const budgetLaborOptions = Object.values(
          projectResponse.data.budgets
            .filter((budget) => !budget.isVariance && !budget.isChangeOrder)
            .flatMap((budget) => budget.lineItems || [])
            .filter((item) => item.category === "L")
            .reduce((acc, item) => {
              const key = `${
                item.itemDescription || item.costCodeDescription || "Unnamed"
              }|${item.uom || ""}`;
              if (
                !acc[key] ||
                item.unitPrice > acc[key].unitPrice ||
                (item.unitPrice === acc[key].unitPrice &&
                  item.itemCode > acc[key].itemCode)
              ) {
                let costCode = item.costCode || null;
                if (!costCode && item.sectionNumber) {
                  const sectionNumber = item.sectionNumber.trim().toLowerCase();
                  const matchingCostCode = costCodesResponse.data.find((cc) =>
                    cc.code.trim().toLowerCase().includes(sectionNumber),
                  );
                  costCode = matchingCostCode ? matchingCostCode._id : null;
                  if (!costCode) {
                    console.warn(
                      `No cost code found for budget labor sectionNumber: ${item.sectionNumber}`,
                    );
                  }
                }
                acc[key] = {
                  value: item._id,
                  label: `${
                    item.itemDescription ||
                    item.costCodeDescription ||
                    "Unnamed"
                  } (Rate: $${(item.unitRate || item.unitPrice || 0).toFixed(
                    2,
                  )} / ${item.uom || "N/A"})`,
                  unitRate: item.unitRate || item.price || 0,
                  unitCost: item.unitRate || item.cost || 0,
                  uom: item.uom || "",
                  itemCode: item.itemCode || "",
                  itemDescription:
                    item.itemDescription ||
                    item.costCodeDescription ||
                    "Unnamed",
                  bidArea: item.bidArea || "",
                  costCode,
                  sectionNumber: item.sectionNumber || "",
                  source: "budget", // Add source to distinguish budget items
                };
              }
              return acc;
            }, {}),
        );

        // Combine contract and budget labor items, avoiding duplicates
        const combinedLaborOptions = [
          ...contractLaborOptions,
          ...budgetLaborOptions.filter(
            (budgetItem) =>
              !contractLaborOptions.some(
                (contractItem) =>
                  contractItem.itemDescription === budgetItem.itemDescription &&
                  contractItem.uom === budgetItem.uom,
              ),
          ),
        ];

        // Set available labor items
        setAvailableLabourItems(combinedLaborOptions);

        // Rest of the useEffect hook remains unchanged
        setAvailableMaterialItems(
          Object.values(
            projectResponse.data.budgets
              .filter((budget) => !budget.isVariance && !budget.isChangeOrder)
              .flatMap((budget) => budget.lineItems || [])
              .filter((item) => item.category === "M")
              .reduce((acc, item) => {
                const key = `${item.itemDescription || "Unnamed"}|${
                  item.size || ""
                }`;
                if (
                  !acc[key] ||
                  item.price > acc[key].price ||
                  (item.price === acc[key].price &&
                    item.itemCode > acc[key].itemCode)
                ) {
                  let costCode = item.costCode || null;
                  if (!costCode && item.sectionNumber) {
                    const sectionNumber = item.sectionNumber
                      .trim()
                      .toLowerCase();
                    const matchingCostCode = costCodesResponse.data.find((cc) =>
                      cc.code.trim().toLowerCase().includes(sectionNumber),
                    );
                    costCode = matchingCostCode ? matchingCostCode._id : null;
                    if (!costCode) {
                      console.warn(
                        `No cost code found for material sectionNumber: ${item.sectionNumber}`,
                      );
                    }
                  }
                  acc[key] = {
                    value: item._id,
                    label: `${
                      item.itemDescription || "Unnamed"
                    } (Price: $${item.price.toFixed(
                      2,
                    )} / Cost: $${item.cost.toFixed(2)} - Size: ${item.size})`,
                    price: item.price,
                    cost: item.cost,
                    itemCode: item.itemCode || "",
                    itemDescription: item.itemDescription || "",
                    size: item.size || "",
                    container: item.container || "",
                    bidArea: item.bidArea || "",
                    vendor: item.vendor?._id || item.vendor || null,
                    costCode,
                    sectionNumber: item.sectionNumber || "",
                  };
                }
                return acc;
              }, {}),
          ),
        );

        const budgetBidAreas = [
          ...new Set(
            projectResponse.data.budgets
              .filter((budget) => !budget.isVariance && !budget.isChangeOrder)
              .flatMap((budget) => budget.lineItems || [])
              .filter((item) => item.bidArea)
              .map((item) => item.bidArea),
          ),
        ];

        const changeOrderBidAreas = [
          ...new Set(
            changeOrderResponse.data.items
              .filter((item) => item.bidArea)
              .map((item) => item.bidArea),
          ),
        ];

        const uniqueBidAreas = [
          ...new Set([...budgetBidAreas, ...changeOrderBidAreas]),
        ].map((bidArea) => ({ value: bidArea, label: bidArea }));
        setBidAreaOptions(uniqueBidAreas);
        if (
          !companyProfileResponse.data ||
          Object.keys(companyProfileResponse.data).length === 0
        ) {
          setAlert({
            open: true,
            type: "error",
            title: "Configuration Error",
            message:
              "Company profile is not set. Please configure it in the settings.",
          });
          setCompanyProfile(null);
        } else {
          setCompanyProfile(companyProfileResponse.data);
        }

        const changeOrder = changeOrderResponse.data;
        setSavedChangeOrder(changeOrder);
        setFormData({
          title: changeOrder.title || "",
          termsAndConditions: changeOrder.termsAndConditions || "",
          approvalStatus: changeOrder.approvalStatus || "Pending",
          overheadPercentage: changeOrder.overheadPercentage || 0,
          changesToSchedule: changeOrder.changesToSchedule || 0,
          timeAndMaterialId: changeOrder.tAndM || null,
        });

        const materialItems = changeOrder.items
          .filter((item) => item.type === "material")
          .map((item) => {
            let costCode = item.costCode?._id || item.costCode || null;
            if (!costCode && item.sectionNumber) {
              const sectionNumber = item.sectionNumber.trim().toLowerCase();
              const matchingCostCode = costCodesResponse.data.find((cc) =>
                cc.code.trim().toLowerCase().includes(sectionNumber),
              );
              costCode = matchingCostCode ? matchingCostCode._id : null;
              if (!costCode) {
                console.warn(
                  `No cost code found for material sectionNumber: ${item.sectionNumber}`,
                );
              }
            }
            return {
              uploadId:
                item._id?.$oid ||
                `uploaded-${Math.random().toString(36).substr(2, 9)}`,
              sectionNumber: item.sectionNumber || "",
              category: item.category || "M",
              costCodeDescription: item.costCodeDescription || "",
              amount: Number(parseFloat(item.amount || 0).toFixed(2)),
              title: item.title || "",
              quantity: Math.ceil(parseFloat(item.quantity) || 0),
              unitCost: Number(parseFloat(item.unitCost || 0).toFixed(2)),
              price: Number(
                parseFloat(item.price || item.unitPrice || 0).toFixed(2),
              ),
              type: "material",
              itemCode: item.itemCode || "",
              itemDescription: item.itemDescription || "",
              size: item.size || "",
              requiredQty: parseFloat(item.requiredQty || 0),
              container: item.container || "",
              bidArea: item.bidArea || "",
              vendor: item.vendor?.$oid || item.vendor || null,
              costCode,
              customer: item.customer !== undefined ? item.customer : true,
              budget: item.budget !== undefined ? item.budget : true,
            };
          });

        const laborItemsData = changeOrder.items
          .filter((item) => item.type === "labor" && item.category !== "S")
          .map((item) => ({
            uploadId:
              item._id?.$oid ||
              `uploaded-${Math.random().toString(36).substr(2, 9)}`,
            labourTitle: item.title || "",
            quantity: Math.ceil(parseFloat(item.quantity) || 0),
            unitCost: Number(parseFloat(item.unitCost || 0).toFixed(2)),
            unitPrice: Number(parseFloat(item.unitPrice || 0).toFixed(2)),
            type: "labor",
            category: item.category || "L",
            costCodeDescription: item.costCodeDescription || "",
            amount: Number(parseFloat(item.amount || 0).toFixed(2)),
            uom: item.uom || "",
            laborHours: parseFloat(item.laborHours || 0),
            bidArea: item.bidArea || "",
            costCode: item.costCode?._id || item.costCode || null,
            customer: item.customer !== undefined ? item.customer : true,
            budget: item.budget !== undefined ? item.budget : true,
          }));

        const surchargeItemsData = changeOrder.items
          .filter((item) => item.type === "labor" && item.category === "S")
          .map((item) => ({
            uploadId:
              item._id?.$oid ||
              `surcharge-${Math.random().toString(36).substr(2, 9)}`,
            title: item.title || "",
            quantity: Math.ceil(parseFloat(item.quantity) || 0),
            unitCost: Number(parseFloat(item.unitCost || 0).toFixed(2)),
            unitPrice: Number(parseFloat(item.unitPrice || 0).toFixed(2)),
            price: Number(parseFloat(item.unitPrice || 0).toFixed(2)),
            type: "labor",
            category: "S",
            costCodeDescription: item.costCodeDescription || "",
            amount: Number(parseFloat(item.amount || 0).toFixed(2)),
            uom: "N/A",
            laborHours: 0,
            bidArea: "",
            costCode: item.costCode?._id || item.costCode || null,
            customer: item.customer !== undefined ? item.customer : true,
            budget: item.budget !== undefined ? item.budget : true,
          }));

        setSelectedMaterials(materialItems);
        setLaborItems(laborItemsData);
        setSurchargeItems(surchargeItemsData);
        setUploadedItems([
          ...materialItems,
          ...laborItemsData,
          ...surchargeItemsData,
        ]);

        const totalCostFromItems = Number(
          (
            materialItems
              .filter((item) => item.budget)
              .reduce((sum, item) => sum + item.unitCost * item.quantity, 0) +
            laborItemsData
              .filter((item) => item.budget)
              .reduce((sum, item) => sum + item.unitCost * item.quantity, 0) +
            surchargeItemsData
              .filter((item) => item.budget)
              .reduce((sum, item) => sum + item.unitCost * item.quantity, 0)
          ).toFixed(2),
        );

        const totalPriceFromItems = Number(
          (
            materialItems
              .filter((item) => item.customer)
              .reduce((sum, item) => sum + item.price * item.quantity, 0) +
            laborItemsData
              .filter((item) => item.customer)
              .reduce((sum, item) => sum + item.unitPrice * item.quantity, 0) +
            surchargeItemsData
              .filter((item) => item.customer)
              .reduce((sum, item) => sum + item.unitPrice * item.quantity, 0)
          ).toFixed(2),
        );

        const markupFromItems =
          totalCostFromItems > 0
            ? Number(
                (
                  ((totalPriceFromItems - totalCostFromItems) /
                    totalCostFromItems) *
                  100
                ).toFixed(2),
              )
            : 0;

        setSummary({
          description: changeOrder.summary?.description || "",
          qtyUnit: changeOrder.summary?.qtyUnit || "",
          totalPriceForCustomer: changeOrder.totalPrice || totalPriceFromItems,
          totalCost: changeOrder.totalCost || totalCostFromItems,
          revision: changeOrder.summary?.revision || 0,
          markup: markupFromItems,
          billable:
            changeOrder.summary?.billable !== undefined
              ? changeOrder.summary.billable
              : true,
        });

        if (changeOrder.tAndM) {
          try {
            const tmResponse = await axios.get(
              `https://erp-production-72da01c8e651.herokuapp.com/api/time-and-material/${id}/${changeOrder.tAndM}`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            setTimeAndMaterialData(tmResponse.data);
          } catch (err) {
            console.error("Error fetching T&M data:", err);
            setAlert({
              open: true,
              type: "error",
              title: "Error",
              message: "Failed to load T&M data: " + err.message,
            });
          }
        }
      } catch (err) {
        setAlert({
          open: true,
          type: "error",
          title: "Data Load Error",
          message:
            "Failed to load data: " +
            (err.response?.data?.message || err.message),
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id, changeOrderId]);

  // useEffect for user email (unchanged)
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");
        const decoded = jwtDecode(token);
        setUserEmail(decoded.email || "");
        const contactEmail = project?.customerContact?.email || "";
        const clientFirstName = project?.customerContact?.firstName || "Client";
        const response = await axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/users/me/${decoded.email}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const displayName = response.data.success
          ? response.data.user.displayName || "User"
          : "User";
        setEmailForm({
          to: contactEmail,
          subject: `${project?.projectName || "Project"} - Change Order #${
            savedChangeOrder?.changeOrderNumber || "Pending"
          }`,
          body: `Dear ${clientFirstName},\n\nPlease find attached the Change Order${
            formData.timeAndMaterialId && timeAndMaterialData ? " and T&M" : ""
          } for your review.\n\nBest regards,`,
          signature: displayName,
        });
      } catch (err) {
        console.error("Failed to fetch user data:", err);
        setAlert({
          open: true,
          type: "error",
          title: "User Data Error",
          message: "Failed to load user information.",
        });
      }
    };
    if (project && savedChangeOrder) {
      fetchUserData();
    }
  }, [
    project,
    savedChangeOrder,
    formData.timeAndMaterialId,
    timeAndMaterialData,
  ]);

  const vendorOptions = vendors.map((vendor) => ({
    value: vendor._id,
    label: vendor.companyName,
  }));
  const costCodeOptions = costCodes.map((costCode) => ({
    value: costCode._id,
    label: `${costCode.code} - ${costCode.description}`,
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "overheadPercentage" || name === "changesToSchedule"
          ? parseFloat(value) || 0
          : value,
    }));
  };

  const isApproved = formData.approvalStatus === "Approved";
  const isRejected = formData.approvalStatus === "Rejected";

  const handleDeleteMaterialItem = (index) => {
    setUploadedItems((prev) => {
      const materialItems = prev.filter((item) => item.type === "material");
      const updatedMaterialItems = materialItems.filter((_, i) => i !== index);
      return [
        ...prev.filter((item) => item.type !== "material"),
        ...updatedMaterialItems,
      ];
    });
  };

  const handleBudgetFileUpload = (e) => {
    e.preventDefault();
    if (!budgetFile) {
      setAlert({
        open: true,
        type: "error",
        title: "Upload Error",
        message: "Please select a change order budget file.",
      });
      return;
    }
    const fileExtension = budgetFile.name.split(".").pop().toLowerCase();
    const processFile = async (data) => {
      const cleanNumber = (value) => {
        if (value == null) return 0;
        const str = String(value).trim();
        const cleaned = str.replace(/[$,]/g, "");
        return parseFloat(cleaned) || 0;
      };

      // Extract bid areas from the file
      const fileBidAreas = [
        ...new Set(
          data
            .filter((row) => row["Bid Area"]?.trim())
            .map((row) => row["Bid Area"].trim()),
        ),
      ].map((bidArea) => ({ value: bidArea, label: bidArea }));

      // Update bidAreaOptions with new bid areas from the file
      setBidAreaOptions((prev) =>
        [
          ...new Set([
            ...prev.map((opt) => opt.value),
            ...fileBidAreas.map((opt) => opt.value),
          ]),
        ].map((value) => ({ value, label: value })),
      );

      const parsedItems = await Promise.all(
        data.map(async (row, index) => {
          if (!row || typeof row !== "object") {
            console.warn(`Row ${index} is invalid or empty:`, row);
            return null;
          }
          let vendorId = null;
          const category = row["Category"]?.trim();
          const amount = cleanNumber(row["Amount"]) || 0;
          const laborQTY = cleanNumber(row["Quantity"]) || 0;
          const sectionNumber = row["Section #"]?.trim() || "";
          const matchingCostCode = costCodes.find((cc) =>
            sectionNumber ? cc.code.includes(sectionNumber) : false,
          );
          const sellingPrice = cleanNumber(
            row["Customer Price"] ||
              row["Customer price"] ||
              row[" Price "] ||
              row["Price"],
          );
          const costCodeId = matchingCostCode
            ? matchingCostCode._id
            : costCodes[0]?._id;
          const bidArea = row["Bid Area"]?.trim() || ""; // Use bidArea directly from file

          if (category === "M") {
            const cost = cleanNumber(row["Price"]) || 0;
            const materialSize = row["Size"] || "";
            const requiredQty = cleanNumber(row["Required Qty"]) || 0;
            const itemCode = row["Item Code"] || "";
            let price = sellingPrice;
            let unitCost = cost;
            if (requiredQty >= 0 && itemCode && project?.budgets?.length > 0) {
              const normalizeSize = (size) => {
                if (!size) return "";
                return size
                  .toLowerCase()
                  .replace(/['"gaftin]+/g, "")
                  .replace(/\s*\(\s*|\s*\)\s*/g, "")
                  .replace(/\s+/g, "")
                  .replace(/fine\s*thread|ss|o\.c/gi, "")
                  .trim();
              };
              const matchingItems = project.budgets
                .filter((budget) => !budget.isVariance && !budget.isChangeOrder)
                .flatMap((budget) => budget.lineItems)
                .filter(
                  (lineItem) =>
                    lineItem.category === "M" &&
                    lineItem.itemCode === itemCode &&
                    lineItem.price > 0 &&
                    lineItem.cost > 0 &&
                    normalizeSize(lineItem.size) ===
                      normalizeSize(materialSize),
                );
              if (matchingItems.length > 0) {
                price = Math.max(...matchingItems.map((item) => item.price));
                unitCost = Math.max(...matchingItems.map((item) => item.cost));
              }
            }
            if (row["Part Number"]?.trim()) {
              const vendorName = row["Part Number"].trim();
              const matchedVendors = vendors.filter((v) =>
                v.companyName.toLowerCase().includes(vendorName.toLowerCase()),
              );
              if (matchedVendors.length === 1) {
                vendorId = matchedVendors[0]._id;
              } else if (matchedVendors.length > 1) {
                console.warn(
                  `Multiple vendors found for '${vendorName}' at row ${index}, using first match`,
                );
                vendorId = matchedVendors[0]._id;
              }
            }
            if (row["Vendor"]?.trim()) {
              const vendorName = row["Vendor"].trim();
              const matchedVendors = vendors.filter((v) =>
                v.companyName.toLowerCase().includes(vendorName.toLowerCase()),
              );
              if (matchedVendors.length === 1) {
                vendorId = matchedVendors[0]._id;
              } else if (matchedVendors.length > 1) {
                console.warn(
                  `Multiple vendors found for '${vendorName}' at row ${index}, using first match`,
                );
                vendorId = matchedVendors[0]._id;
              }
            }
            let quantity =
              requiredQty === 0 && price > 0 && amount !== 0
                ? Math.ceil(amount / price)
                : Math.ceil(requiredQty || 1);
            const computedAmount =
              requiredQty < 0
                ? amount
                : Number((quantity * unitCost).toFixed(2));
            return {
              uploadId: `uploaded-${index}`,
              sectionNumber,
              category: "M",
              costCodeDescription: row["Cost Code Description"] || "",
              amount: computedAmount,
              title:
                row["Item Description"] || row["Cost Code Description"] || "",
              quantity,
              unitCost: Number(unitCost.toFixed(2)),
              price: Number(price.toFixed(2)),
              cost: Number(unitCost.toFixed(2)),
              type: "material",
              itemCode,
              itemDescription: row["Item Description"] || "",
              size: row["Size"] || "",
              requiredQty: quantity,
              container: row["Container"] || "",
              bidArea, // Use bidArea from file
              vendor: vendorId,
              costCode: costCodeId,
              customer: true,
              budget: true,
            };
          } else if (category === "L") {
            const computedQuantity = laborQTY !== 0 ? laborQTY : 1;
            const unitCost =
              amount !== 0 && laborQTY !== 0
                ? Number((amount / laborQTY).toFixed(2))
                : 0;
            const computedAmount =
              laborQTY < 0 ? amount : Number((laborQTY * unitCost).toFixed(2));
            return {
              uploadId: `uploaded-${index}`,
              labourTitle: row["Cost Code Description"] || "",
              quantity: computedQuantity,
              unitCost,
              unitPrice: unitCost,
              type: "labor",
              category: "L",
              costCodeDescription: row["Cost Code Description"] || "",
              amount: computedAmount,
              uom: row["UOM"] || "",
              laborHours: cleanNumber(row["Labor Hours"]) || 0,
              bidArea, // Use bidArea from file
              costCode: costCodeId,
              sectionNumber,
              customer: true,
              budget: true,
            };
          } else if (category === "S") {
            const computedQuantity = laborQTY !== 0 ? laborQTY : 1;
            const unitCost = cleanNumber(row["Unit Cost"]) || 0;
            const unitPrice = cleanNumber(row["Unit Price"]) || unitCost;
            const computedAmount =
              computedQuantity < 0
                ? amount
                : Number((computedQuantity * unitCost).toFixed(2));
            return {
              uploadId: `surcharge-${index}`,
              title:
                row["Cost Code Description"] || row["Item Description"] || "",
              quantity: computedQuantity,
              unitCost: Number(unitCost.toFixed(2)),
              unitPrice: Number(unitPrice.toFixed(2)),
              price: Number(unitPrice.toFixed(2)),
              type: "labor",
              category: "S",
              costCodeDescription: row["Cost Code Description"] || "",
              amount: computedAmount,
              uom: "N/A",
              laborHours: 0,
              bidArea: "",
              costCode: costCodeId,
              sectionNumber,
              customer: true,
              budget: true,
            };
          } else {
            console.warn(`Row ${index} has invalid or missing Category:`, row);
            return null;
          }
        }),
      );
      const filteredItems = parsedItems.filter(
        (item) => item != null && item.type != null,
      );
      if (filteredItems.length === 0) {
        setAlert({
          open: true,
          type: "error",
          title: "Error",
          message:
            "No valid items found in the uploaded file. Please check the file format and data.",
        });
        return;
      }
      setUploadedItems((prev) => [...prev, ...filteredItems]);
      setLaborItems((prev) => [
        ...prev,
        ...filteredItems.filter(
          (item) => item.type === "labor" && item.category !== "S",
        ),
      ]);
      setSurchargeItems((prev) => [
        ...prev,
        ...filteredItems.filter(
          (item) => item.type === "labor" && item.category === "S",
        ),
      ]);
      setSelectedMaterials((prev) => [
        ...prev,
        ...filteredItems.filter((item) => item.type === "material"),
      ]);
      setBudgetFile(null);
      setShowBudgetUpload(false);
      setAlert({
        open: true,
        type: "success",
        title: "Upload Success",
        message:
          "Budget uploaded successfully! Click 'Update Change Order' to save.",
      });
    };
    if (fileExtension === "csv") {
      Papa.parse(budgetFile, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          if (!result.data || result.data.length === 0) {
            setAlert({
              open: true,
              type: "error",
              title: "Error",
              message: "The CSV file is empty or invalid.",
            });
            return;
          }
          processFile(result.data);
        },
        error: (err) =>
          setAlert({
            open: true,
            type: "error",
            title: "Parse Error",
            message: "Error parsing CSV file: " + err.message,
          }),
      });
    } else if (fileExtension === "xlsx") {
      const reader = new FileReader();
      reader.onload = (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        if (!jsonData || jsonData.length === 0) {
          setAlert({
            open: true,
            type: "error",
            title: "Error",
            message: "The Excel file is empty or invalid.",
          });
          return;
        }
        processFile(jsonData);
      };
      reader.onerror = () =>
        setAlert({
          open: true,
          type: "error",
          title: "Read Error",
          message: "Error reading XLSX file.",
        });
      reader.readAsArrayBuffer(budgetFile);
    } else {
      setAlert({
        open: true,
        type: "error",
        title: "Format Error",
        message: "Unsupported file format. Please upload a .csv or .xlsx file.",
      });
    }
  };

  const handleMaterialChange = (index, field, value) => {
    setUploadedItems((prev) => {
      const materialItems = prev.filter((item) => item.type === "material");
      const updatedMaterialItems = [...materialItems];
      const newValue =
        field === "quantity" ||
        field === "bidArea" ||
        field === "vendor" ||
        field === "costCode" ||
        field === "customer" ||
        field === "budget"
          ? field === "quantity"
            ? Math.ceil(parseFloat(value) || 0)
            : field === "customer" || field === "budget"
              ? value
              : value
          : Number(parseFloat(value).toFixed(2)) || 0;
      updatedMaterialItems[index] = {
        ...updatedMaterialItems[index],
        [field]: newValue,
        ...(field === "quantity" && {
          amount: Number(
            (newValue * updatedMaterialItems[index].unitCost).toFixed(2),
          ),
          requiredQty: newValue,
        }),
        ...(field === "unitCost" && {
          amount: Number(
            (updatedMaterialItems[index].quantity * newValue).toFixed(2),
          ),
        }),
        ...(field === "price" && { price: newValue }),
      };
      return [
        ...prev.filter((item) => item.type !== "material"),
        ...updatedMaterialItems,
      ];
    });
  };
  const handleNewMaterialChange = (e) => {
    const { name, value } = e.target;

    setNewMaterialItem((prev) => {
      let updates = {
        [name]:
          name === "quantity"
            ? Math.ceil(parseFloat(value) || 0)
            : name === "unitCost" || name === "price"
              ? Number(parseFloat(value).toFixed(2)) || 0
              : value,
      };

      // Always keep description in sync with title (live update)
      if (name === "title") {
        updates.itemDescription = value.trim();
      }

      return {
        ...prev,
        ...updates,
      };
    });
  };

  const handleLaborChange = (e) => {
    const { name, value } = e.target;
    setNewLaborItem((prev) => ({
      ...prev,
      [name]:
        name === "quantity"
          ? Math.ceil(parseFloat(value) || 0)
          : name === "unitCost" || name === "unitPrice"
            ? Number(parseFloat(value).toFixed(2)) || 0
            : value,
    }));
  };

  const handleSurchargeChange = (e) => {
    const { name, value } = e.target;
    setNewSurchargeItem((prev) => ({
      ...prev,
      [name]:
        name === "quantity"
          ? Math.ceil(parseFloat(value) || 0)
          : name === "unitCost" || name === "unitPrice"
            ? Number(parseFloat(value).toFixed(2)) || 0
            : value,
    }));
  };

  const handleLaborItemChange = (index, field, value) => {
    setLaborItems((prev) => {
      const updated = [...prev];
      const newValue =
        field === "quantity" ||
        field === "bidArea" ||
        field === "costCode" ||
        field === "customer" ||
        field === "budget"
          ? field === "quantity"
            ? Math.ceil(parseFloat(value) || 0)
            : field === "customer" || field === "budget"
              ? value
              : value
          : Number(parseFloat(value).toFixed(2)) || 0;
      updated[index] = {
        ...updated[index],
        [field === "labourTitle" ? "labourTitle" : field]: newValue,
        ...(field === "labourTitle" && { costCodeDescription: value }),
        amount:
          field === "quantity" || field === "unitCost"
            ? Number(
                (
                  (field === "quantity" ? newValue : updated[index].quantity) *
                  (field === "unitCost" ? newValue : updated[index].unitCost)
                ).toFixed(2),
              )
            : updated[index].amount,
        ...(field === "unitPrice" && { unitPrice: newValue }),
      };
      return updated;
    });
  };

  const handleSurchargeItemChange = (index, field, value) => {
    setSurchargeItems((prev) => {
      const updated = [...prev];
      const newValue =
        field === "quantity" ||
        field === "costCode" ||
        field === "customer" ||
        field === "budget"
          ? field === "quantity"
            ? Math.ceil(parseFloat(value) || 0)
            : field === "customer" || field === "budget"
              ? value
              : value
          : Number(parseFloat(value).toFixed(2)) || 0;
      updated[index] = {
        ...updated[index],
        [field]: newValue,
        amount:
          field === "quantity" || field === "unitCost"
            ? Number(
                (
                  (field === "quantity" ? newValue : updated[index].quantity) *
                  (field === "unitCost" ? newValue : updated[index].unitCost)
                ).toFixed(2),
              )
            : updated[index].amount,
        ...(field === "unitPrice" && { unitPrice: newValue, price: newValue }),
      };
      return updated;
    });
  };

  // New handler for adding material items
  const handleAddMaterialItem = () => {
    const errors = [];

    if (!newMaterialItem.title?.trim()) errors.push("Title is required");
    if (!newMaterialItem.size?.trim()) errors.push("Size / Style is required");
    if (!newMaterialItem.costCode) errors.push("Cost Code is required");
    if (!newMaterialItem.bidArea?.trim()) errors.push("Bid Area is required");
    if (!newMaterialItem.vendor) errors.push("Vendor is required");
    if (newMaterialItem.unitCost <= 0) errors.push("Unit Cost > 0");
    if (newMaterialItem.price <= 0) errors.push("Unit Price > 0");

    if (errors.length > 0) {
      setAlert({
        open: true,
        type: "error",
        title: "Missing Required Fields",
        message: errors.join(" • "),
      });
      return;
    }

    const newItem = {
      uploadId: `material-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      title: newMaterialItem.title.trim(),
      quantity: newMaterialItem.quantity,
      unitCost: newMaterialItem.unitCost,
      price: newMaterialItem.price,
      type: "material",
      category: "M",
      costCodeDescription: newMaterialItem.title.trim(),
      amount: Number(
        (newMaterialItem.quantity * newMaterialItem.unitCost).toFixed(2),
      ),
      itemCode: newMaterialItem.itemCode?.trim() || "", // optional
      itemDescription:
        newMaterialItem.itemDescription?.trim() || newMaterialItem.title.trim(),
      size: newMaterialItem.size.trim(),
      container: newMaterialItem.container?.trim() || "",
      bidArea: newMaterialItem.bidArea.trim(),
      vendor: newMaterialItem.vendor,
      costCode: newMaterialItem.costCode,
      sectionNumber:
        newMaterialItem.sectionNumber ||
        costCodes
          .find((cc) => cc._id === newMaterialItem.costCode)
          ?.code.split(" - ")[0]
          ?.trim() ||
        "",
      customer: newMaterialItem.customer,
      budget: newMaterialItem.budget,
      requiredQty: newMaterialItem.quantity,
    };

    setUploadedItems((prev) => [...prev, newItem]);
    setNewMaterialItem({
      title: "",
      quantity: 0,
      unitCost: 0,
      price: 0,
      vendor: null,
      costCode: "",
      bidArea: "",
      itemCode: "",
      itemDescription: "",
      size: "",
      container: "",
      customer: true,
      budget: true,
    });
    setSelectedMaterialOption(null);
    setShowMaterialInput(false);

    setAlert({
      open: true,
      type: "success",
      title: "Added",
      message: "Material item added successfully!",
    });
  };
  const handleAddLaborItem = () => {
    if (
      !newLaborItem.labourTitle ||
      newLaborItem.unitCost <= 0 ||
      newLaborItem.unitPrice <= 0 ||
      !newLaborItem.costCode
    ) {
      setAlert({
        open: true,
        type: "error",
        title: "Validation Error",
        message:
          "Labour description, quantity, unit cost, unit price, and cost code are required.",
      });
      return;
    }
    const newItem = {
      uploadId: `manual-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`,
      labourTitle: newLaborItem.labourTitle,
      quantity: newLaborItem.quantity,
      unitCost: newLaborItem.unitCost,
      unitPrice: newLaborItem.unitPrice,
      type: "labor",
      category: "L",
      costCodeDescription: newLaborItem.labourTitle,
      amount: Number(
        (newLaborItem.quantity * newLaborItem.unitCost).toFixed(2),
      ),
      uom: newLaborItem.uom,
      laborHours: 0,
      bidArea: newLaborItem.bidArea,
      costCode: newLaborItem.costCode,
      sectionNumber:
        costCodes
          .find((cc) => cc._id === newLaborItem.costCode)
          ?.code.split(" - ")[0] || "",
      customer: newLaborItem.customer,
      budget: newLaborItem.budget,
    };
    setLaborItems((prev) => [...prev, newItem]);
    setUploadedItems((prev) => [...prev, newItem]);
    setNewLaborItem({
      labourTitle: "",
      quantity: 0,
      unitCost: 0,
      unitPrice: 0,
      uom: "",
      bidArea: "",
      costCode: "",
      customer: true,
      budget: true,
    });
    setSelectedLabourOption(null);
    setShowLaborInput(false);
    setAlert({
      open: true,
      type: "success",
      title: "Success",
      message: "Labor item added successfully!",
    });
  };

  const handleAddSurchargeItem = () => {
    if (
      !newSurchargeItem.title ||
      newSurchargeItem.unitCost <= 0 ||
      newSurchargeItem.unitPrice <= 0 ||
      !newSurchargeItem.costCode
    ) {
      setAlert({
        open: true,
        type: "error",
        title: "Validation Error",
        message:
          "Title, quantity, unit cost, unit price, and cost code are required for surcharges.",
      });
      return;
    }
    const newItem = {
      uploadId: `surcharge-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`,
      title: newSurchargeItem.title,
      quantity: newSurchargeItem.quantity,
      unitCost: newSurchargeItem.unitCost,
      unitPrice: newSurchargeItem.unitPrice,
      price: newSurchargeItem.unitPrice,
      type: "labor",
      category: "S",
      costCodeDescription: newSurchargeItem.title,
      amount: Number(
        (newSurchargeItem.quantity * newSurchargeItem.unitCost).toFixed(2),
      ),
      uom: "N/A",
      laborHours: 0,
      bidArea: "",
      costCode: newSurchargeItem.costCode,
      sectionNumber:
        costCodes
          .find((cc) => cc._id === newSurchargeItem.costCode)
          ?.code.split(" - ")[0] || "",
      customer: newSurchargeItem.customer,
      budget: newSurchargeItem.budget,
      surcharge: true,
    };
    setSurchargeItems((prev) => [...prev, newItem]);
    setUploadedItems((prev) => [...prev, newItem]);
    setNewSurchargeItem({
      title: "",
      quantity: 0,
      unitCost: 0,
      unitPrice: 0,
      costCode: "",
      customer: true,
      budget: true,
    });
    setShowSurchargeInput(false);
    setAlert({
      open: true,
      type: "success",
      title: "Success",
      message: "Surcharge item added successfully!",
    });
  };

  const handleDeleteLaborItem = (index) => {
    setLaborItems((prev) => prev.filter((_, i) => i !== index));
    setUploadedItems((prev) =>
      prev.filter(
        (item) =>
          !(
            item.type === "labor" &&
            item.category !== "S" &&
            prev.indexOf(item) === index
          ),
      ),
    );
  };

  const handleDeleteSurchargeItem = (index) => {
    setSurchargeItems((prev) => prev.filter((_, i) => i !== index));
    setUploadedItems((prev) =>
      prev.filter(
        (item) =>
          !(
            item.type === "labor" &&
            item.category === "S" &&
            prev.indexOf(item) === index
          ),
      ),
    );
  };

  // New handler for material item selection
  const handleMaterialItemSelect = (selectedOption) => {
    setSelectedMaterialOption(selectedOption);

    if (selectedOption) {
      let costCode = selectedOption.costCode || null;
      if (!costCode && selectedOption.sectionNumber) {
        const sectionNumber = selectedOption.sectionNumber.trim().toLowerCase();
        const matchingCostCode = costCodes.find((cc) =>
          cc.code?.trim().toLowerCase().includes(sectionNumber),
        );
        costCode = matchingCostCode ? matchingCostCode._id : null;
        if (!costCode) {
          console.warn(
            `No cost code found for sectionNumber: ${selectedOption.sectionNumber}`,
          );
        }
      }

      // Debug log to confirm itemCode is coming through
      console.log("Selected material:", {
        label: selectedOption.label,
        itemCode: selectedOption.itemCode,
        itemDescription: selectedOption.itemDescription,
      });

      setNewMaterialItem((prev) => ({
        ...prev,
        title: selectedOption.label?.split(" (")[0] || "",
        unitCost: selectedOption.cost || 0,
        price: selectedOption.price || 0,
        itemCode: selectedOption.itemCode || "", // ← this should now load
        itemDescription:
          selectedOption.itemDescription?.trim() ||
          (selectedOption.label?.split(" (")[0] || "").trim(),
        size: selectedOption.size || "",
        container: selectedOption.container || "",
        bidArea: selectedOption.bidArea || "",
        vendor: selectedOption.vendor || null,
        costCode,
        sectionNumber: selectedOption.sectionNumber || "",
      }));
    } else {
      setNewMaterialItem({
        title: "",
        quantity: 0,
        unitCost: 0,
        price: 0,
        vendor: null,
        costCode: "",
        bidArea: "",
        itemCode: "",
        itemDescription: "",
        size: "",
        container: "",
        customer: true,
        budget: true,
      });
    }
  };

  const getUserEmailFromToken = () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        return decoded.email;
      } catch (err) {
        console.error("Error decoding token:", err);
        return null;
      }
    }
    return null;
  };

  const totalCost = Number(
    (
      uploadedItems
        .filter((item) => item.type === "material" && item.budget)
        .reduce((sum, item) => sum + (item.unitCost * item.quantity || 0), 0) +
      laborItems
        .filter((item) => item.budget)
        .reduce((sum, item) => sum + (item.unitCost * item.quantity || 0), 0) +
      surchargeItems
        .filter((item) => item.budget)
        .reduce((sum, item) => sum + (item.unitCost * item.quantity || 0), 0)
    ).toFixed(2),
  );
  const totalPriceForCustomerWithoutOH = Number(
    (
      uploadedItems
        .filter((item) => item.type === "material" && item.customer)
        .reduce((sum, item) => sum + (item.price * item.quantity || 0), 0) +
      laborItems
        .filter((item) => item.customer)
        .reduce((sum, item) => sum + (item.unitPrice * item.quantity || 0), 0) +
      surchargeItems
        .filter((item) => item.customer)
        .reduce((sum, item) => sum + (item.unitPrice * item.quantity || 0), 0)
    ).toFixed(2),
  );
  const overheadAmount =
    totalPriceForCustomerWithoutOH * (formData.overheadPercentage / 100);
  const totalPriceForCustomer = Number(
    (totalPriceForCustomerWithoutOH + overheadAmount).toFixed(2),
  );
  const markup =
    totalCost > 0
      ? Number(
          (((totalPriceForCustomer - totalCost) / totalCost) * 100).toFixed(2),
        )
      : 0;

  // Updated handleLabourItemSelect function
  const handleLabourItemSelect = (selectedOption) => {
    console.log(selectedOption);
    setSelectedLabourOption(selectedOption);
    if (selectedOption) {
      let costCode = selectedOption.costCode || null;
      if (!costCode && selectedOption.sectionNumber) {
        const sectionNumber = selectedOption.sectionNumber.trim().toLowerCase();
        const matchingCostCode = costCodes.find((cc) =>
          cc.code?.trim().toLowerCase().includes(sectionNumber),
        );
        costCode = matchingCostCode ? matchingCostCode._id : null;
        if (!costCode) {
          console.warn(
            `No cost code found for labor sectionNumber: ${selectedOption.sectionNumber}`,
          );
        }
      }
      setNewLaborItem((prev) => ({
        ...prev,
        labourTitle: selectedOption.label?.split(" (")[0] || "",
        unitCost: Number(
          parseFloat(
            selectedOption.source === "budget"
              ? selectedOption.unitRate || 0
              : selectedOption.unitCost || 0,
          ).toFixed(2),
        ),
        unitPrice: Number(
          parseFloat(
            selectedOption.source === "budget"
              ? selectedOption.unitRate || 0
              : selectedOption.unitRate || 0,
          ).toFixed(2),
        ),
        uom: selectedOption.uom || "",
        bidArea: selectedOption.bidArea || "",
        costCode,
        sectionNumber: selectedOption.sectionNumber || "",
      }));
    } else {
      setNewLaborItem({
        labourTitle: "",
        quantity: 0,
        unitCost: 0,
        unitPrice: 0,
        uom: "",
        bidArea: "",
        costCode: "",
        customer: true,
        budget: true,
        sectionNumber: "",
      });
    }
  };

  const handleBillableToggle = () => {
    setSummary((prev) => ({ ...prev, billable: !prev.billable }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const userEmail = getUserEmailFromToken();
    if (!userEmail) {
      setAlert({
        open: true,
        type: "error",
        title: "Authentication Error",
        message: "User email not found. Please log in again.",
      });
      return;
    }
    if (
      !formData.title ||
      uploadedItems.filter((item) => item.type === "material").length +
        laborItems.length +
        surchargeItems.length ===
        0
    ) {
      setAlert({
        open: true,
        type: "error",
        title: "Validation Error",
        message: "Title and at least one item are required.",
      });
      return;
    }
    const allItems = [
      ...uploadedItems.filter((item) => item.type === "material"),
      ...laborItems,
      ...surchargeItems,
    ];
    const invalidItems = allItems.some((item, index) => {
      const reasons = [];
      if (!item.type) {
        reasons.push(`Item ${index} has no type`);
      }
      if (item.quantity !== undefined && isNaN(item.quantity)) {
        reasons.push(`Item ${index} has invalid quantity: ${item.quantity})`);
      }
      if (item.type === "material") {
        if (isNaN(item.amount)) {
          reasons.push(
            `Item ${index} (material) has invalid amount: ${item.amount} (must be a number)`,
          );
        }
        if (isNaN(item.price)) {
          reasons.push(
            `Item ${index} (material) has invalid price: ${item.price} (must be a number)`,
          );
        }
      }
      if (item.type === "labor") {
        if (isNaN(item.unitCost)) {
          reasons.push(
            `Item ${index} (labor) has invalid unitCost: ${item.unitCost} (must be a number)`,
          );
        }
        if (isNaN(item.unitPrice)) {
          reasons.push(
            `Item ${index} (labor) has invalid unitPrice: ${item.unitPrice} (must be a number)`,
          );
        }
      }
      if (reasons.length > 0) {
        console.log(
          `Invalid item at index ${index}:`,
          JSON.stringify(item, null, 2),
        );
        console.log("Reasons:", reasons);
        return true;
      }
      return false;
    });
    if (invalidItems) {
      setAlert({
        open: true,
        type: "error",
        title: "Validation Error",
        message:
          "All items must have valid type, quantity, cost, and price values. Quantity must be positive, and costs/prices must be valid numbers.",
      });
      return;
    }
    if (isNaN(totalPriceForCustomer)) {
      setAlert({
        open: true,
        type: "error",
        title: "Validation Error",
        message: "Total price for customer is invalid.",
      });
      return;
    }
    if (isNaN(totalCost)) {
      setAlert({
        open: true,
        type: "error",
        title: "Validation Error",
        message: "Total cost is invalid.",
      });
      return;
    }
    if (isRejected && savedChangeOrder?.approvalStatus === "Rejected") {
      if (formData.approvalStatus !== "Pending") {
        setAlert({
          open: true,
          type: "error",
          title: "Update Bound",
          message:
            "A Rejected Change Order can only be updated by first changing its status to Pending.",
        });
        return;
      }
    }
    let changeOrderData;
    if (isApproved && savedChangeOrder?.approvalStatus === "Approved") {
      changeOrderData = {
        summary: {
          billable: summary.billable,
          description: summary.description,
          qtyUnit: summary.qtyUnit,
          revision: summary.revision, // Include revision
        },
        userEmail,
      };
    } else {
      changeOrderData = {
        title: formData.title,
        termsAndConditions: formData.termsAndConditions,
        approvalStatus: formData.approvalStatus,
        overheadPercentage: Number(formData.overheadPercentage) || 0,
        changesToSchedule: Number(formData.changesToSchedule) || 0,
        totalPrice: Number(totalPriceForCustomer.toFixed(2)),
        totalCost: Number(totalCost.toFixed(2)),
        items: [
          ...uploadedItems
            .filter((item) => item.type === "material")
            .map((item) => ({
              title: item.title,
              amount: Number(item.amount.toFixed(2)),
              quantity: Number(item.quantity) || 0,
              price: Number(item.price.toFixed(2)),
              unitCost: Number(item.unitCost.toFixed(2)),
              unitPrice: Number(item.price.toFixed(2)),
              vendor: item.vendor || null,
              costCode: item.costCode || null,
              sectionNumber: item.sectionNumber || "",
              category: item.category || "M",
              itemCode: item.itemCode || "",
              itemDescription: item.itemDescription || "",
              size: item.size || "",
              requiredQty: Number(item.requiredQty) || 0,
              container: item.container || "",
              bidArea: item.bidArea || "",
              customer: item.customer !== undefined ? item.customer : true,
              budget: item.budget !== undefined ? item.budget : true,
              type: "material",
            })),
          ...laborItems.map((item) => ({
            title: item.labourTitle,
            quantity: Number(item.quantity) || 0,
            unitCost: Number(item.unitCost.toFixed(2)),
            unitPrice: Number(item.unitPrice.toFixed(2)),
            price: Number(item.unitPrice.toFixed(2)),
            costCode: item.costCode || null,
            sectionNumber: item.sectionNumber || "",
            category: item.category || "L",
            uom: item.uom || "",
            laborHours: Number(item.laborHours) || 0,
            bidArea: item.bidArea || "",
            customer: item.customer !== undefined ? item.customer : true,
            budget: item.budget !== undefined ? item.budget : true,
            type: "labor",
          })),
          ...surchargeItems.map((item) => ({
            title: item.title,
            quantity: Number(item.quantity) || 0,
            unitCost: Number(item.unitCost.toFixed(2)),
            unitPrice: Number(item.unitPrice.toFixed(2)),
            price: Number(item.unitPrice.toFixed(2)),
            costCode: item.costCode || null,
            sectionNumber:
              costCodes
                .find((cc) => cc._id === item.costCode)
                ?.code.split(" - ")[0] || "",
            category: "S",
            uom: "N/A",
            laborHours: 0,
            bidArea: "",
            itemCode: "",
            itemDescription: item.title,
            customer: item.customer !== undefined ? item.customer : true,
            budget: item.budget !== undefined ? item.budget : true,
            surcharge: true,
            type: "labor",
          })),
        ],
        summary: {
          description: summary.description,
          qtyUnit: summary.qtyUnit,
          billable: summary.billable,
          revision: summary.revision, // Include revision
        },
        project: id,
        userEmail,
      };
    }
    if (
      savedChangeOrder?.approvalStatus === "Approved" &&
      formData.approvalStatus === "Rejected"
    ) {
      setPendingSubmitData(changeOrderData);
      setShowConfirmationModal(true);
      return;
    }
    await submitChangeOrder(changeOrderData);
  };

  const submitChangeOrder = async (changeOrderData) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      let endpoint;
      switch (formData.approvalStatus) {
        case "Approved":
          endpoint = `https://erp-production-72da01c8e651.herokuapp.com/api/changeOrders/${changeOrderId}/approve`;
          break;
        case "Rejected":
          endpoint = `https://erp-production-72da01c8e651.herokuapp.com/api/changeOrders/${changeOrderId}/reject`;
          break;
        default:
          endpoint = `https://erp-production-72da01c8e651.herokuapp.com/api/changeOrders/${changeOrderId}`;
      }
      const response = await axios.put(endpoint, changeOrderData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let message = `Change Order ${
        formData.approvalStatus === "Pending"
          ? "updated"
          : formData.approvalStatus.toLowerCase()
      } with number: ${
        response.data.changeOrderNumber ||
        response.data.changeOrder?.changeOrderNumber
      }`;
      if (
        formData.approvalStatus === "Rejected" &&
        response.data.remainingLineItemsCount > 0
      ) {
        message += `. ${response.data.remainingLineItemsCount} budget item(s) remain due to linked POs/WOs.`;
      }
      setSavedChangeOrder(response.data);
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: message,
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Update Error",
        message: `Failed to update change order: ${
          err.response?.data?.message || err.message
        }`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmReject = () => {
    if (pendingSubmitData) {
      submitChangeOrder(pendingSubmitData);
    }
    setShowConfirmationModal(false);
    setPendingSubmitData(null);
  };

  const handleCancelReject = () => {
    setShowConfirmationModal(false);
    setPendingSubmitData(null);
  };

  const handleSummaryChange = (e) => {
    const { name, value } = e.target;
    setSummary((prev) => ({
      ...prev,
      [name]:
        name === "revision"
          ? Math.max(0, Math.floor(parseFloat(value) || 0))
          : value,
    }));
  };

  const formatNumber = (num) =>
    Number.isFinite(num)
      ? `$${num.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : "$0.00";

  const generateTandMPDF = async (tmData) => {
    if (!companyProfile || !tmData) {
      throw new Error(
        !companyProfile
          ? "Cannot generate T&M PDF: Company profile is not set."
          : "Cannot generate T&M PDF: T&M data is missing.",
      );
    }

    const response = await fetch("/t_and_m_template.docx");
    if (!response.ok)
      throw new Error(`Failed to load T&M template: ${response.statusText}`);
    const templateArrayBuffer = await response.arrayBuffer();
    const zip = new PizZip(templateArrayBuffer);

    const base64Regex = /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
    const validBase64 =
      /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

    function base64Parser(tagValue) {
      if (typeof tagValue !== "string" || !base64Regex.test(tagValue))
        return null;
      const stringBase64 = tagValue.replace(base64Regex, "");
      if (!validBase64.test(stringBase64))
        throw new Error("Invalid base64 data");
      const binaryString = window.atob(stringBase64);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);
      return bytes.buffer;
    }

    const imageOptions = {
      getImage: (tagValue) => base64Parser(tagValue),
      getSize: (img, tagValue, tagName) => {
        if (tagName === "companyLogo") return [150, 100];
        if (tagName === "signature") return [100, 150];
        if (tagName === "image") return [200, 200];
        return [150, 100];
      },
    };
    const imageModule = new ImageModule(imageOptions);
    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      modules: [imageModule],
    });

    let companyLogoBase64 = null;
    if (companyProfile?.logo && !companyProfile.logo.startsWith("data:image")) {
      try {
        const logoResponse = await fetch(companyProfile.logo, { mode: "cors" });
        if (!logoResponse.ok)
          throw new Error(`Failed to fetch logo: ${logoResponse.statusText}`);
        const logoBlob = await logoResponse.blob();
        companyLogoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(logoBlob);
        });
      } catch (err) {
        console.error("Error fetching company logo:", err.message);
        companyLogoBase64 = null;
      }
    } else if (companyProfile?.logo) {
      companyLogoBase64 = companyProfile.logo;
    }

    let signatureBase64 = null;
    if (tmData.signature) {
      try {
        const sigResponse = await fetch(tmData.signature, { mode: "cors" });
        if (!sigResponse.ok)
          throw new Error(
            `Failed to fetch signature: ${sigResponse.statusText}`,
          );
        const sigBlob = await sigResponse.blob();
        signatureBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(sigBlob);
        });
      } catch (err) {
        console.warn("Failed to fetch signature:", err.message);
        signatureBase64 = null;
      }
    }

    const imagesPromises = (tmData.images || []).map(async (url, index) => {
      let imageBase64 = null;
      try {
        const imgResponse = await fetch(url, { mode: "cors" });
        if (!imgResponse.ok)
          throw new Error(`Failed to fetch image: ${imgResponse.statusText}`);
        const imgBlob = await imgResponse.blob();
        imageBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(imgBlob);
        });
      } catch (err) {
        console.warn(`Failed to fetch image ${index + 1}: ${err.message}`);
        imageBase64 = null;
      }
      return { image: imageBase64 };
    });

    const resolvedImages = await Promise.all(imagesPromises);

    // Process labour items (only customer-facing)
    const labourItems = (tmData.labourItems || [])
      .filter((item) => item && item.customer === true)
      .map((item, index) => {
        const unitPrice =
          item.quantityUsed > 0 ? item.totalPrice / item.quantityUsed : 0;
        const title =
          item.contractLabourItem?.labourItem?.title ||
          availableLabourItems
            .find((opt) => opt.value === item.contractLabourItem?._id)
            ?.label.split(" (")[0] ||
          "Unnamed";
        return {
          itemNo: index + 1,
          description: title,
          quantity: item.quantityUsed || 0,
          price: unitPrice.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
          amount: (item.totalPrice || 0).toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
        };
      });

    // Process material items (only customer-facing)
    const materialItems = (tmData.materialItems || [])
      .filter((item) => item && item.customer === true)
      .map((item, index) => {
        const materialOption = availableMaterialItems.find(
          (opt) =>
            opt.value === (item.budgetLineItem?._id || item.budgetLineItem),
        );
        const description = `${
          item.itemDescription || materialOption?.itemDescription || "Unnamed"
        } (${item.container || materialOption?.container || "N/A"} - ${
          item.size || materialOption?.size || "N/A"
        })`;
        const unitPrice =
          item.quantityUsed > 0 ? item.totalPrice / item.quantityUsed : 0;
        return {
          itemNo: labourItems.length + index + 1,
          description,
          quantity: item.quantityUsed || 0,
          price: unitPrice.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
          amount: (item.totalPrice || 0).toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
        };
      });

    // Process surcharge items (only customer-facing)
    const surchargeItems = (tmData.surchargeItems || [])
      .filter((item) => item && item.customer === true)
      .map((item, index) => {
        const unitPrice =
          item.quantityUsed > 0 ? item.totalPrice / item.quantityUsed : 0;
        return {
          itemNo: labourItems.length + materialItems.length + index + 1,
          description: item.title || "Unnamed Surcharge",
          quantity: item.quantityUsed || 0,
          price: unitPrice.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
          amount: (item.totalPrice || 0).toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
        };
      });

    // Combine all customer-facing items
    const items = [...labourItems, ...materialItems, ...surchargeItems];

    if (items.length === 0) {
      setAlert({
        open: true,
        type: "warning",
        title: "Warning",
        message: "No customer-facing items to include in the T&M PDF.",
      });
      return;
    }

    // Calculate financials for customer-facing items
    const subtotalPrice = items.reduce((sum, item) => {
      const amount = parseFloat(item.amount.replace(/[^0-9.-]+/g, "")) || 0;
      return sum + amount;
    }, 0);
    const overheadAmount =
      subtotalPrice * ((tmData.overheadPercentage || 0) / 100);
    const taxRate = parseFloat(companyProfile?.defaultTax?.taxRate) || 0;
    const taxAmount = subtotalPrice * (taxRate / 100);
    const totalPrice = subtotalPrice + taxAmount + overheadAmount;

    const data = {
      projectName: project?.projectName || "Unknown Project",
      date: new Date(
        tmData.signedAt || tmData.createdAt || Date.now(),
      ).toLocaleDateString(),
      preparedBy: tmData.createdBy?.displayName || "Unknown",
      timeAndMaterialNumber: tmData.timeAndMaterialNumber || "Unknown",
      scopeOfWork: tmData.scopeOfWork || "N/A",
      signedBy: tmData.signedBy || "N/A",
      companyName: companyProfile?.companyName || "Your Company Name",
      companyAddress: companyProfile?.address
        ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}, ${companyProfile.address.country}`
        : "N/A",
      companyLogo: companyLogoBase64 || null,
      signature: signatureBase64 || null,
      items,
      images: resolvedImages.filter((img) => img.image != null),
      subtotalPrice: subtotalPrice.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      }),
      taxRate: taxRate.toFixed(2),
      taxAmount: taxAmount.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      }),
      overheadPercentage: (tmData.overheadPercentage || 0).toFixed(2),
      overheadAmount: overheadAmount.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      }),
      totalPrice: totalPrice.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      }),
    };

    doc.setData(data);
    try {
      doc.render();
    } catch (renderError) {
      console.error("Error rendering T&M Docxtemplater:", renderError);
      throw new Error("Failed to render T&M document template.");
    }

    const docxBlob = doc.getZip().generate({
      type: "blob",
      mimeType:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

    const formDataPDF = new FormData();
    formDataPDF.append("docx", docxBlob, `TandM_${tmData._id}.docx`);
    formDataPDF.append("documentType", "TandM");
    formDataPDF.append("identifier", tmData._id);
    const token = localStorage.getItem("token");
    const pdfResponse = await axios.post(
      "https://erp-production-72da01c8e651.herokuapp.com/api/upload/convert-to-pdf",
      formDataPDF,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        responseType: "blob",
      },
    );

    if (
      pdfResponse.status === 200 &&
      pdfResponse.headers["content-type"] === "application/pdf"
    ) {
      return new Blob([pdfResponse.data], { type: "application/pdf" });
    } else {
      throw new Error("Failed to convert T&M DOCX to PDF");
    }
  };

  const generateChangeOrderPDF = async (sendEmail = false) => {
    if (!companyProfile) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Cannot generate PDF: Company profile is not set.",
      });
      return;
    }

    // Custom currency formatting fgunction
    const formatCurrency = (value) => {
      const absValue = Math.abs(value);
      const formatted = absValue.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return value < 0 ? `(${formatted})` : formatted;
    };

    try {
      const response = await fetch("/change_order_template.docx");
      if (!response.ok)
        throw new Error(`Failed to load template: ${response.statusText}`);
      const templateArrayBuffer = await response.arrayBuffer();
      const zip = new PizZip(templateArrayBuffer);
      const base64Regex =
        /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
      const validBase64 =
        /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
      function base64Parser(tagValue) {
        if (typeof tagValue !== "string" || !base64Regex.test(tagValue))
          return null;
        const stringBase64 = tagValue.replace(base64Regex, "");
        if (!validBase64.test(stringBase64))
          throw new Error("Error parsing base64 data, invalid characters");
        const binaryString = window.atob(stringBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);
        return bytes.buffer;
      }
      let imageBase64;
      if (
        companyProfile?.logo &&
        !companyProfile.logo.startsWith("data:image")
      ) {
        const logoResponse = await fetch(companyProfile.logo);
        if (logoResponse.ok) {
          const logoBlob = await logoResponse.blob();
          imageBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(logoBlob);
          });
        }
      } else if (companyProfile?.logo) {
        imageBase64 = companyProfile.logo;
      }
      const imageOptions = {
        getImage: (tagValue) => base64Parser(tagValue),
        getSize: () => [125, 90],
      };
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        modules: [new ImageModule(imageOptions)],
      });
      const materialSubtotal = uploadedItems
        .filter((item) => item.type === "material" && item.customer)
        .reduce((sum, item) => sum + item.quantity * item.price, 0);
      const labourTotal = laborItems
        .filter((item) => item.customer)
        .reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
      const surchargeTotal = surchargeItems
        .filter((item) => item.customer)
        .reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
      const subtotal = materialSubtotal + labourTotal + surchargeTotal;
      const overhead = subtotal * (Number(formData.overheadPercentage) / 100);
      const total = totalPriceForCustomer;
      const projectAccount =
        project?.account?.companyName ||
        project?.accountId?.companyName ||
        "Unknown Contractor";
      const customerContact = project?.customerContact
        ? `${project.customerContact.firstName} ${project.customerContact.lastName}`
        : "N/A";
      const data = {
        changeOrderNumber:
          summary?.revision > 0
            ? `${savedChangeOrder?.changeOrderNumber} - R${summary.revision}`
            : savedChangeOrder?.changeOrderNumber || "TBD",
        title: formData.title,
        projectName: project?.projectName || "Unknown Project",
        date: new Intl.DateTimeFormat("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
        }).format(new Date()),
        projectAccount,
        customerContact,
        description: summary.description || "N/A",
        materials: uploadedItems
          .filter((item) => item.type === "material" && item.customer)
          .map((item) => ({
            title: item.title,
            unitPrice: formatCurrency(item.price), // Use custom formatting
            materialQuantity: item.quantity, // Keep quantity unformatted
            itemTotal: formatCurrency(item.quantity * item.price), // Use custom formatting
          })),
        materialSubtotal: formatCurrency(materialSubtotal), // Use custom formatting
        labourItems: laborItems
          .filter((item) => item.customer)
          .map((item) => ({
            labourTitle: item.labourTitle,
            labourQuantity: item.quantity, // Keep quantity unformatted
            labourRate: formatCurrency(item.unitPrice), // Use custom formatting
            labourItemTotal: formatCurrency(item.quantity * item.unitPrice), // Use custom formatting
          })),
        labourTotal: formatCurrency(labourTotal), // Use custom formatting
        surchargeItems: surchargeItems
          .filter((item) => item.customer)
          .map((item) => ({
            title: item.title,
            quantity: item.quantity, // Keep quantity unformatted
            unitPrice: formatCurrency(item.unitPrice), // Use custom formatting
            itemTotal: formatCurrency(item.quantity * item.unitPrice), // Use custom formatting
          })),
        surchargeTotal: formatCurrency(surchargeTotal), // Use custom formatting
        companyName: companyProfile?.companyName || "Your Company Name",
        companyAddress: companyProfile?.address
          ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}, ${companyProfile.address.country}`
          : "N/A",
        subtotal: formatCurrency(subtotal), // Use custom formatting
        ohPrecent: formData.overheadPercentage.toFixed(2),
        overhead: formatCurrency(overhead), // Use custom formatting
        total: formatCurrency(total), // Use custom formatting
        changestoSchedule: formData.changesToSchedule,
        termsAndConditions: formData.termsAndConditions || "N/A",
        image: imageBase64,
      };
      doc.setData(data);
      doc.render();
      const docxBlob = doc.getZip().generate({
        type: "blob",
        mimeType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const formDataPDF = new FormData();
      formDataPDF.append(
        "docx",
        docxBlob,
        `ChangeOrder_${data.changeOrderNumber}.docx`,
      );
      formDataPDF.append("documentType", "ChangeOrder");
      formDataPDF.append("identifier", data.changeOrderNumber);
      const token = localStorage.getItem("token");
      const pdfResponse = await axios.post(
        "https://erp-production-72da01c8e651.herokuapp.com/api/upload/convert-to-pdf",
        formDataPDF,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob",
        },
      );
      if (
        pdfResponse.status !== 200 ||
        pdfResponse.headers["content-type"] !== "application/pdf"
      ) {
        throw new Error("Failed to convert Change Order DOCX to PDF");
      }
      let coPdfBlob = new Blob([pdfResponse.data], { type: "application/pdf" });
      let tmPdfBlob = null;
      if (formData.timeAndMaterialId && timeAndMaterialData) {
        tmPdfBlob = await generateTandMPDF(timeAndMaterialData);
      }
      if (sendEmail) {
        setPdfBuffers({ co: coPdfBlob, tm: tmPdfBlob });
        setShowEmailForm(true);
      } else {
        let finalPdfBlob = coPdfBlob;
        if (tmPdfBlob) {
          const coPdf = await PDFDocument.load(await coPdfBlob.arrayBuffer());
          const tmPdf = await PDFDocument.load(await tmPdfBlob.arrayBuffer());
          const mergedPdf = await PDFDocument.create();
          const coPages = await mergedPdf.copyPages(
            coPdf,
            coPdf.getPageIndices(),
          );
          const tmPages = await mergedPdf.copyPages(
            tmPdf,
            tmPdf.getPageIndices(),
          );
          coPages.forEach((page) => mergedPdf.addPage(page));
          tmPages.forEach((page) => mergedPdf.addPage(page));
          const mergedPdfBytes = await mergedPdf.save();
          finalPdfBlob = new Blob([mergedPdfBytes], {
            type: "application/pdf",
          });
        }
        saveAs(
          finalPdfBlob,
          `ChangeOrder_${data.changeOrderNumber}${
            tmPdfBlob ? "_with_TM" : ""
          }.pdf`,
        );
        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: tmPdfBlob
            ? "Change Order and T&M PDF generated and merged successfully!"
            : "Change Order PDF generated successfully!",
        });
      }
    } catch (err) {
      console.error("PDF generation error:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to generate PDF: " + err.message,
      });
    }
  };

  const handleSendEmail = () => {
    if (!userEmail) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "User email not available. Please log in again.",
      });
      return;
    }
    if (!savedChangeOrder?._id) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Please save the Change Order before sending an email.",
      });
      return;
    }
    generateChangeOrderPDF(true);
  };

  const handleEmailSubmit = async () => {
    try {
      if (!emailForm.to || !emailForm.subject || !emailForm.body) {
        setAlert({
          open: true,
          type: "error",
          title: "Invalid Input",
          message: "Please fill in all required email fields",
        });
        return;
      }
      const validateEmails = (emails) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emails
          .split(",")
          .map((email) => email.trim())
          .every((email) => emailRegex.test(email));
      };
      if (!validateEmails(emailForm.to)) {
        setAlert({
          open: true,
          type: "error",
          title: "Invalid Email",
          message: "Please enter valid email addresses",
        });
        return;
      }
      if (!pdfBuffers.co || pdfBuffers.co.type !== "application/pdf") {
        console.error("Invalid CO pdfBuffer:", pdfBuffers.co);
        setAlert({
          open: true,
          type: "error",
          title: "Missing or Invalid Attachment",
          message: "A valid Change Order PDF is required",
        });
        return;
      }
      if (timeAndMaterialData && formData.timeAndMaterialId) {
        if (!pdfBuffers.tm || pdfBuffers.tm.type !== "application/pdf") {
          console.error("Invalid T&M pdfBuffer:", pdfBuffers.tm);
          setAlert({
            open: true,
            type: "error",
            title: "Missing or Invalid Attachment",
            message: "A valid T&M PDF is required",
          });
          return;
        }
      }
      const token = localStorage.getItem("token");
      const emailFormData = new FormData();
      emailFormData.append("to", emailForm.to);
      emailFormData.append("subject", emailForm.subject);
      emailFormData.append("body", emailForm.body);
      emailFormData.append("signature", emailForm.signature || "");
      emailFormData.append("from", userEmail);
      emailFormData.append(
        "files",
        pdfBuffers.co,
        `ChangeOrder_${savedChangeOrder?.changeOrderNumber || "Pending"}.pdf`,
      );
      if (pdfBuffers.tm) {
        emailFormData.append(
          "files",
          pdfBuffers.tm,
          `TandM_${timeAndMaterialData?.timeAndMaterialNumber || "Pending"}.pdf`,
        );
      }
      const response = await axios.post(
        `https://erp-production-72da01c8e651.herokuapp.com/api/changeOrders/${changeOrderId}/send-email`,
        emailFormData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setShowEmailForm(false);
      setPdfBuffers({ co: null, tm: null });
      if (response.status === 200) {
        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: "Email sent successfully!",
        });
      }
    } catch (err) {
      console.error("Email error:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: `Failed to send email: ${
          err.response?.data?.message || err.message
        }`,
      });
    }
  };

  if (isLoading)
    return <div className="text-center py-4 text-gray-200">Loading...</div>;
  if (!project)
    return (
      <div className="text-red-400 text-center py-4">Project not found.</div>
    );
  return (
    <div className="bg-gray-300 p-4 rounded-lg shadow-md border border-gray-300 mt-4">
      <div className="bg-gray-700 rounded-lg p-4">
        <AlertDialog
          type={alert.type}
          title={alert.title}
          message={alert.message}
          open={alert.open}
          onClose={() => setAlert({ ...alert, open: false })}
          onConfirm={() => setAlert({ ...alert, open: false })}
          confirmText="OK"
          cancelText="Close"
        />
        {!companyProfile && (
          <div className="bg-gray-600 border-l-4 border-yellow-400 text-yellow-400 p-4 mb-6">
            <p>
              Warning: Company profile is not configured. Some features (e.g.,
              PDF generation) may be unavailable.
            </p>
          </div>
        )}
        {isApproved && savedChangeOrder?.approvalStatus === "Approved" && (
          <div className="bg-gray-600 border-l-4 border-blue-400 text-blue-400 p-4 mb-6">
            <p>
              This Change Order is Approved. Only the billable status can be
              modified.
            </p>
          </div>
        )}
        {isRejected && savedChangeOrder?.approvalStatus === "Rejected" && (
          <div className="bg-gray-600 border-l-4 border-red-400 text-red-400 p-4 mb-6">
            <p>
              This Change Order is Rejected. Change the status to Pending to
              enable editing.
            </p>
          </div>
        )}
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-semibold text-white tracking-tight">
            Edit Change Quotation #{savedChangeOrder?.changeOrderNumber}
          </h1>
          <button
            onClick={() =>
              navigate(`/projects/${id}`, {
                state: { initialTab: "Change Order (CO)" },
              })
            }
            className="inline-flex items-center px-4 py-2 bg-gray-600 text-gray-200 text-sm font-semibold rounded-md hover:bg-gray-500 transition-colors duration-200"
          >
            <ArrowLeftIcon className="size-4 mr-2" />
            Back
          </button>
        </div>
        {showEmailForm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-700 p-6 rounded-lg shadow-lg w-full max-w-lg border border-gray-600">
              <h2 className="text-xl font-semibold text-orange-400 mb-4">
                Send Email
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-200">
                    From
                  </label>
                  <input
                    type="email"
                    value={userEmail}
                    disabled
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-200">
                    To
                  </label>
                  <input
                    type="text"
                    value={emailForm.to}
                    onChange={(e) =>
                      setEmailForm({ ...emailForm, to: e.target.value })
                    }
                    placeholder="recipient@example.com, another@example.com"
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-200">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={emailForm.subject}
                    onChange={(e) =>
                      setEmailForm({ ...emailForm, subject: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-200">
                    Body
                  </label>
                  <textarea
                    value={emailForm.body}
                    onChange={(e) =>
                      setEmailForm({ ...emailForm, body: e.target.value })
                    }
                    rows="5"
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-200">
                    Signature
                  </label>
                  <textarea
                    value={emailForm.signature}
                    onChange={(e) =>
                      setEmailForm({ ...emailForm, signature: e.target.value })
                    }
                    rows="3"
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmailForm(false);
                      setPdfBuffers({ co: null, tm: null });
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleEmailSubmit}
                    className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                    disabled={!pdfBuffers.co}
                  >
                    Send Email
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
            <h2 className="text-lg font-semibold text-white mb-3">
              Change Order Details
            </h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-6 md:gap-x-4 md:gap-y-3">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-200">
                  Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Title"
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  required
                  disabled={
                    (isApproved &&
                      savedChangeOrder?.approvalStatus === "Approved") ||
                    (isRejected &&
                      savedChangeOrder?.approvalStatus === "Rejected")
                  }
                />
              </div>
              <div className="flex space-x-1 md:col-span-2">
                <div>
                  <label className="block text-sm font-medium text-gray-200">
                    Overhead & Profit (%)
                  </label>
                  <input
                    type="number"
                    name="overheadPercentage"
                    value={formData.overheadPercentage}
                    onChange={handleChange}
                    placeholder="0"
                    step="0.01"
                    className="mt-1 block w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    disabled={
                      (isApproved &&
                        savedChangeOrder?.approvalStatus === "Approved") ||
                      (isRejected &&
                        savedChangeOrder?.approvalStatus === "Rejected")
                    }
                  />
                </div>
                <div className="px-4">
                  <label className="block text-sm font-medium text-gray-200">
                    Changed (Days)
                  </label>
                  <input
                    type="number"
                    name="changesToSchedule"
                    value={formData.changesToSchedule}
                    onChange={handleChange}
                    placeholder="0"
                    step="1"
                    className="mt-1 block w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    disabled={
                      (isApproved &&
                        savedChangeOrder?.approvalStatus === "Approved") ||
                      (isRejected &&
                        savedChangeOrder?.approvalStatus === "Rejected")
                    }
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-200">
                  Approval Status
                </label>
                <select
                  name="approvalStatus"
                  value={formData.approvalStatus}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                >
                  {savedChangeOrder?.approvalStatus === "Approved" ? (
                    <>
                      <option value="Approved">Approved</option>
                      <option value="Rejected">Rejected</option>
                    </>
                  ) : (
                    <>
                      <option value="Pending">Pending</option>
                      <option value="Approved">Approved</option>
                      <option value="Rejected">Rejected</option>
                    </>
                  )}
                </select>
              </div>
              <div className="md:col-span-4">
                <label className="block text-sm font-medium text-gray-200">
                  Terms and Conditions
                </label>
                <textarea
                  name="termsAndConditions"
                  value={formData.termsAndConditions}
                  onChange={handleChange}
                  placeholder="Terms and Conditions"
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  rows="2"
                  disabled={
                    (isApproved &&
                      savedChangeOrder?.approvalStatus === "Approved") ||
                    (isRejected &&
                      savedChangeOrder?.approvalStatus === "Rejected")
                  }
                />
              </div>
            </div>
          </div>
          <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-white">Materials</h2>
              <div className="space-x-4">
                <button
                  type="button"
                  onClick={() => setShowMaterialInput(!showMaterialInput)}
                  className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                  disabled={
                    (isApproved &&
                      savedChangeOrder?.approvalStatus === "Approved") ||
                    (isRejected &&
                      savedChangeOrder?.approvalStatus === "Rejected")
                  }
                >
                  {showMaterialInput ? "Hide Add Material" : "Add Line Item"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowBudgetUpload(!showBudgetUpload)}
                  className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                  disabled={
                    (isApproved &&
                      savedChangeOrder?.approvalStatus === "Approved") ||
                    (isRejected &&
                      savedChangeOrder?.approvalStatus === "Rejected")
                  }
                >
                  {showBudgetUpload
                    ? "Hide Upload"
                    : "Upload Change Order Budget"}
                </button>
              </div>
            </div>
            {showBudgetUpload && (
              <div className="space-y-4 mb-4">
                <input
                  type="file"
                  accept=".csv, .xlsx"
                  onChange={(e) => setBudgetFile(e.target.files[0])}
                  className="block w-full text-sm text-gray-200 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-600 file:text-gray-200 hover:file:bg-gray-500"
                  disabled={
                    (isApproved &&
                      savedChangeOrder?.approvalStatus === "Approved") ||
                    (isRejected &&
                      savedChangeOrder?.approvalStatus === "Rejected")
                  }
                />
                <button
                  onClick={handleBudgetFileUpload}
                  disabled={
                    !budgetFile ||
                    (isApproved &&
                      savedChangeOrder?.approvalStatus === "Approved") ||
                    (isRejected &&
                      savedChangeOrder?.approvalStatus === "Rejected")
                  }
                  className={`px-4 py-2 text-sm font-medium text-gray-200 rounded-md ${
                    budgetFile &&
                    !(
                      isApproved &&
                      savedChangeOrder?.approvalStatus === "Approved"
                    ) &&
                    !(
                      isRejected &&
                      savedChangeOrder?.approvalStatus === "Rejected"
                    )
                      ? "bg-orange-400 hover:bg-orange-500 transition-colors duration-200"
                      : "bg-gray-600 cursor-not-allowed"
                  }`}
                >
                  Upload Budget
                </button>
              </div>
            )}
            {showMaterialInput && (
              <div className="space-y-4 mb-4">
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-200 mt-1">
                      Select Material Item
                    </label>
                    <Select
                      options={availableMaterialItems}
                      value={selectedMaterialOption}
                      onChange={handleMaterialItemSelect}
                      placeholder="Select a material item"
                      styles={customStyles}
                      isClearable
                      menuPortalTarget={document.body}
                      isDisabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-200">
                      Title
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={newMaterialItem.title}
                      onChange={handleNewMaterialChange}
                      placeholder="Material Title"
                      className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Cost Code
                    </label>
                    <Select
                      options={costCodeOptions}
                      value={
                        costCodeOptions.find(
                          (option) => option.value === newMaterialItem.costCode,
                        ) || null
                      }
                      onChange={(option) =>
                        setNewMaterialItem((prev) => ({
                          ...prev,
                          costCode: option ? option.value : "",
                        }))
                      }
                      placeholder="Select Cost Code"
                      styles={customStyles}
                      className="w-48 mt-1"
                      isDisabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                      menuPortalTarget={document.body}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Bid Area
                    </label>
                    <CreatableSelect
                      options={bidAreaOptions}
                      value={
                        bidAreaOptions.find(
                          (option) => option.value === newMaterialItem.bidArea,
                        ) ||
                        (newMaterialItem.bidArea
                          ? {
                              value: newMaterialItem.bidArea,
                              label: newMaterialItem.bidArea,
                            }
                          : null)
                      }
                      onChange={(option) =>
                        setNewMaterialItem((prev) => ({
                          ...prev,
                          bidArea: option ? option.value : "",
                        }))
                      }
                      onCreateOption={(inputValue) => {
                        const newOption = {
                          value: inputValue,
                          label: inputValue,
                        };
                        setBidAreaOptions((prev) => [...prev, newOption]);
                        setNewMaterialItem((prev) => ({
                          ...prev,
                          bidArea: inputValue,
                        }));
                      }}
                      placeholder="Select or type Bid Area"
                      styles={customStyles}
                      className="w-48 mt-1"
                      isDisabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                      menuPortalTarget={document.body}
                      formatCreateLabel={(inputValue) =>
                        `Create "${inputValue}"`
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Vendor
                    </label>
                    <Select
                      options={vendorOptions}
                      value={
                        vendorOptions.find(
                          (option) => option.value === newMaterialItem.vendor,
                        ) || null
                      }
                      onChange={(option) =>
                        setNewMaterialItem((prev) => ({
                          ...prev,
                          vendor: option ? option.value : null,
                        }))
                      }
                      placeholder="Select Vendor"
                      styles={customStyles}
                      className="w-48 mt-1"
                      isDisabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                      menuPortalTarget={document.body}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Quantity
                    </label>
                    <input
                      type="number"
                      name="quantity"
                      value={newMaterialItem.quantity}
                      onChange={handleNewMaterialChange}
                      placeholder="Quantity"
                      step="1"
                      className="mt-1 w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Unit Cost
                    </label>
                    <input
                      type="number"
                      name="unitCost"
                      value={newMaterialItem.unitCost}
                      onChange={handleNewMaterialChange}
                      placeholder="Unit Cost"
                      step="0.01"
                      className="mt-1 w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Unit Price
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={newMaterialItem.price}
                      onChange={handleNewMaterialChange}
                      placeholder="Unit Price"
                      step="0.01"
                      className="mt-1 w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Item Code
                    </label>
                    <input
                      type="text"
                      name="itemCode"
                      value={newMaterialItem.itemCode}
                      onChange={handleNewMaterialChange}
                      placeholder="Item Code"
                      className="mt-1 w-24 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Description
                    </label>
                    <input
                      type="text"
                      name="itemDescription"
                      value={newMaterialItem.itemDescription}
                      onChange={handleNewMaterialChange}
                      placeholder="Description"
                      className="mt-1 w-48 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Size
                    </label>
                    <input
                      type="text"
                      name="size"
                      value={newMaterialItem.size}
                      onChange={handleNewMaterialChange}
                      placeholder="Size"
                      className="mt-1 w-24 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Container
                    </label>
                    <input
                      type="text"
                      name="container"
                      value={newMaterialItem.container}
                      onChange={handleNewMaterialChange}
                      placeholder="Container"
                      className="mt-1 w-24 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Customer
                    </label>
                    <input
                      type="checkbox"
                      name="customer"
                      checked={newMaterialItem.customer}
                      onChange={(e) =>
                        setNewMaterialItem((prev) => ({
                          ...prev,
                          customer: e.target.checked,
                        }))
                      }
                      className="mt-1 h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Budget
                    </label>
                    <input
                      type="checkbox"
                      name="budget"
                      checked={newMaterialItem.budget}
                      onChange={(e) =>
                        setNewMaterialItem((prev) => ({
                          ...prev,
                          budget: e.target.checked,
                        }))
                      }
                      className="mt-1 h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={handleAddMaterialItem}
                      className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Budget
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Cost Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Bid Area
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Vendor
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Unit Cost
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Unit Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Item Total Cost
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Item Total Price
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-600">
                  {uploadedItems.length === 0 ||
                  !uploadedItems.some((item) => item.type === "material") ? (
                    <tr>
                      <td
                        colSpan="13"
                        className="px-6 py-4 text-center text-sm text-gray-400"
                      >
                        No materials available. Please upload a change order
                        budget file or add a material item manually.
                      </td>
                    </tr>
                  ) : (
                    uploadedItems
                      .filter((item) => item.type === "material")
                      .map((item, index) => (
                        <tr
                          key={item.uploadId}
                          className="hover:bg-gray-500 transition-colors duration-200"
                        >
                          <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                            <input
                              type="checkbox"
                              checked={item.customer}
                              onChange={(e) =>
                                handleMaterialChange(
                                  index,
                                  "customer",
                                  e.target.checked,
                                )
                              }
                              className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                              disabled={
                                (isApproved &&
                                  savedChangeOrder?.approvalStatus ===
                                    "Approved") ||
                                (isRejected &&
                                  savedChangeOrder?.approvalStatus ===
                                    "Rejected")
                              }
                            />
                          </td>
                          <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                            <input
                              type="checkbox"
                              checked={item.budget}
                              onChange={(e) =>
                                handleMaterialChange(
                                  index,
                                  "budget",
                                  e.target.checked,
                                )
                              }
                              className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                              disabled={
                                (isApproved &&
                                  savedChangeOrder?.approvalStatus ===
                                    "Approved") ||
                                (isRejected &&
                                  savedChangeOrder?.approvalStatus ===
                                    "Rejected")
                              }
                            />
                          </td>
                          <td className="px-2 py-4 text-sm text-gray-200 max-w-md break-words whitespace-normal">
                            {item.title}
                          </td>
                          <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                            <Select
                              options={costCodeOptions}
                              value={
                                costCodeOptions.find(
                                  (option) => option.value === item.costCode,
                                ) || null
                              }
                              onChange={(option) =>
                                handleMaterialChange(
                                  index,
                                  "costCode",
                                  option ? option.value : null,
                                )
                              }
                              placeholder="Select Cost Code"
                              styles={customStyles}
                              className="w-32"
                              isDisabled={
                                (isApproved &&
                                  savedChangeOrder?.approvalStatus ===
                                    "Approved") ||
                                (isRejected &&
                                  savedChangeOrder?.approvalStatus ===
                                    "Rejected")
                              }
                              menuPortalTarget={document.body}
                            />
                          </td>
                          <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                            <Select
                              options={bidAreaOptions}
                              value={
                                bidAreaOptions.find(
                                  (option) => option.value === item.bidArea,
                                ) || null
                              }
                              onChange={(option) =>
                                handleMaterialChange(
                                  index,
                                  "bidArea",
                                  option ? option.value : "",
                                )
                              }
                              placeholder="Select Bid Area"
                              styles={customStyles}
                              className="w-32"
                              isDisabled={
                                (isApproved &&
                                  savedChangeOrder?.approvalStatus ===
                                    "Approved") ||
                                (isRejected &&
                                  savedChangeOrder?.approvalStatus ===
                                    "Rejected")
                              }
                              menuPortalTarget={document.body}
                            />
                          </td>
                          <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                            <Select
                              options={vendorOptions}
                              value={
                                vendorOptions.find(
                                  (option) => option.value === item.vendor,
                                ) || null
                              }
                              onChange={(option) =>
                                handleMaterialChange(
                                  index,
                                  "vendor",
                                  option ? option.value : null,
                                )
                              }
                              placeholder="Select Vendor"
                              styles={customStyles}
                              className="w-32"
                              isDisabled={
                                (isApproved &&
                                  savedChangeOrder?.approvalStatus ===
                                    "Approved") ||
                                (isRejected &&
                                  savedChangeOrder?.approvalStatus ===
                                    "Rejected")
                              }
                              menuPortalTarget={document.body}
                            />
                          </td>
                          <td className="px-2 py-4 text-sm text-gray-200 max-w-md break-words whitespace-normal">
                            {item.size}
                          </td>
                          <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                            <input
                              type="number"
                              step="1"
                              value={item.quantity}
                              onChange={(e) =>
                                handleMaterialChange(
                                  index,
                                  "quantity",
                                  e.target.value,
                                )
                              }
                              className="w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                              disabled={
                                (isApproved &&
                                  savedChangeOrder?.approvalStatus ===
                                    "Approved") ||
                                (isRejected &&
                                  savedChangeOrder?.approvalStatus ===
                                    "Rejected")
                              }
                            />
                          </td>
                          <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                            <input
                              type="number"
                              step="0.01"
                              value={item.unitCost}
                              onChange={(e) =>
                                handleMaterialChange(
                                  index,
                                  "unitCost",
                                  e.target.value,
                                )
                              }
                              className="w-24 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                              disabled={
                                (isApproved &&
                                  savedChangeOrder?.approvalStatus ===
                                    "Approved") ||
                                (isRejected &&
                                  savedChangeOrder?.approvalStatus ===
                                    "Rejected")
                              }
                            />
                          </td>
                          <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                            <input
                              type="number"
                              step="0.01"
                              value={item.price}
                              onChange={(e) =>
                                handleMaterialChange(
                                  index,
                                  "price",
                                  e.target.value,
                                )
                              }
                              className="w-24 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                              disabled={
                                (isApproved &&
                                  savedChangeOrder?.approvalStatus ===
                                    "Approved") ||
                                (isRejected &&
                                  savedChangeOrder?.approvalStatus ===
                                    "Rejected")
                              }
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                            {formatNumber(item.quantity * item.unitCost)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                            {formatNumber(item.quantity * item.price)}
                          </td>
                          <td className="px-2 py-4 whitespace-nowrap text-sm text-right">
                            <button
                              type="button"
                              onClick={() => handleDeleteMaterialItem(index)}
                              className="text-red-400 hover:text-red-300 transition-colors duration-200"
                              disabled={
                                (isApproved &&
                                  savedChangeOrder?.approvalStatus ===
                                    "Approved") ||
                                (isRejected &&
                                  savedChangeOrder?.approvalStatus ===
                                    "Rejected")
                              }
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-800">
                    <td
                      colSpan="10"
                      className="px-6 py-3 text-right text-sm font-medium text-gray-200"
                    >
                      Total:
                    </td>
                    <td className="px-6 py-3 text-left text-sm font-medium text-gray-200">
                      {formatNumber(
                        uploadedItems
                          .filter(
                            (item) => item.type === "material" && item.budget,
                          )
                          .reduce(
                            (sum, item) => sum + item.quantity * item.unitCost,
                            0,
                          ),
                      )}
                    </td>
                    <td className="px-6 py-3 text-left text-sm font-medium text-gray-200">
                      {formatNumber(
                        uploadedItems
                          .filter(
                            (item) => item.type === "material" && item.customer,
                          )
                          .reduce(
                            (sum, item) => sum + item.quantity * item.price,
                            0,
                          ),
                      )}
                    </td>
                    <td className="px-2 py-3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 overflow-x-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-white">Labor</h2>
              <button
                type="button"
                onClick={() => setShowLaborInput(!showLaborInput)}
                className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                disabled={
                  (isApproved &&
                    savedChangeOrder?.approvalStatus === "Approved") ||
                  (isRejected &&
                    savedChangeOrder?.approvalStatus === "Rejected")
                }
              >
                {showLaborInput ? "Hide Add Labor" : "Add Line Item"}
              </button>
            </div>
            {showLaborInput && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-4 pb-4">
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-200 mt-1">
                      Select Labor Item
                    </label>
                    <Select
                      options={availableLabourItems}
                      value={selectedLabourOption}
                      onChange={handleLabourItemSelect}
                      placeholder="Select a labor item"
                      styles={customStyles}
                      isClearable
                      menuPortalTarget={document.body}
                      isDisabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-200">
                      Description
                    </label>
                    <input
                      type="text"
                      name="labourTitle"
                      value={newLaborItem.labourTitle}
                      onChange={handleLaborChange}
                      placeholder="Labour Description"
                      className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Cost Code
                    </label>
                    <Select
                      options={costCodeOptions}
                      value={
                        costCodeOptions.find(
                          (option) => option.value === newLaborItem.costCode,
                        ) || null
                      }
                      onChange={(option) =>
                        setNewLaborItem((prev) => ({
                          ...prev,
                          costCode: option ? option.value : "",
                        }))
                      }
                      placeholder="Select Cost Code"
                      styles={customStyles}
                      className="w-48 mt-1"
                      isDisabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                      menuPortalTarget={document.body}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Quantity
                    </label>
                    <input
                      type="number"
                      name="quantity"
                      value={newLaborItem.quantity}
                      onChange={handleLaborChange}
                      placeholder="Quantity"
                      step="1"
                      className="mt-1 w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Unit
                    </label>
                    <input
                      type="text"
                      name="uom"
                      value={newLaborItem.uom}
                      onChange={handleLaborChange}
                      placeholder="Unit"
                      className="mt-1 w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Unit Cost
                    </label>
                    <input
                      type="number"
                      name="unitCost"
                      value={newLaborItem.unitCost}
                      onChange={handleLaborChange}
                      placeholder="Unit Cost"
                      step="0.01"
                      className="mt-1 w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Unit Price
                    </label>
                    <input
                      type="number"
                      name="unitPrice"
                      value={newLaborItem.unitPrice}
                      onChange={handleLaborChange}
                      placeholder="Unit Price"
                      step="0.01"
                      className="mt-1 w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Customer
                    </label>
                    <input
                      type="checkbox"
                      name="customer"
                      checked={newLaborItem.customer}
                      onChange={(e) =>
                        setNewLaborItem((prev) => ({
                          ...prev,
                          customer: e.target.checked,
                        }))
                      }
                      className="mt-1 h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Budget
                    </label>
                    <input
                      type="checkbox"
                      name="budget"
                      checked={newLaborItem.budget}
                      onChange={(e) =>
                        setNewLaborItem((prev) => ({
                          ...prev,
                          budget: e.target.checked,
                        }))
                      }
                      className="mt-1 h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={handleAddLaborItem}
                      className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            )}
            <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-1 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-12">
                    Customer
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-12">
                    Budget
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-80">
                    Description
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-32">
                    Cost Code
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-20">
                    Quantity
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-20">
                    Unit
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-32">
                    Bid Area
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-20">
                    Unit Cost
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-20">
                    Unit Price
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-20">
                    Item Total Cost
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-20">
                    Item Total Price
                  </th>
                  <th className="px-2 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                {laborItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan="12"
                      className="px-6 py-4 text-center text-sm text-gray-400"
                    >
                      No labor items added. Add items manually below or upload a
                      budget file.
                    </td>
                  </tr>
                ) : (
                  laborItems.map((item, index) => (
                    <tr
                      key={item.uploadId || index}
                      className="hover:bg-gray-500 transition-colors duration-200"
                    >
                      <td className="px-1 py-4 whitespace-nowrap text-sm text-gray-200">
                        <input
                          type="checkbox"
                          checked={item.customer}
                          onChange={(e) =>
                            handleLaborItemChange(
                              index,
                              "customer",
                              e.target.checked,
                            )
                          }
                          className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                          disabled={
                            (isApproved &&
                              savedChangeOrder?.approvalStatus ===
                                "Approved") ||
                            (isRejected &&
                              savedChangeOrder?.approvalStatus === "Rejected")
                          }
                        />
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                        <input
                          type="checkbox"
                          checked={item.budget}
                          onChange={(e) =>
                            handleLaborItemChange(
                              index,
                              "budget",
                              e.target.checked,
                            )
                          }
                          className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                          disabled={
                            (isApproved &&
                              savedChangeOrder?.approvalStatus ===
                                "Approved") ||
                            (isRejected &&
                              savedChangeOrder?.approvalStatus === "Rejected")
                          }
                        />
                      </td>
                      <td
                        className="px-2 py-4 text-sm text-gray-200 truncate max-w-[350px]"
                        title={item.labourTitle}
                      >
                        {item.labourTitle}
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                        <Select
                          options={costCodeOptions}
                          value={
                            costCodeOptions.find(
                              (option) => option.value === item.costCode,
                            ) || null
                          }
                          onChange={(option) =>
                            handleLaborItemChange(
                              index,
                              "costCode",
                              option ? option.value : null,
                            )
                          }
                          placeholder="Select Cost Code"
                          styles={customStyles}
                          className="w-32"
                          isDisabled={
                            (isApproved &&
                              savedChangeOrder?.approvalStatus ===
                                "Approved") ||
                            (isRejected &&
                              savedChangeOrder?.approvalStatus === "Rejected")
                          }
                        />
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                        <input
                          type="number"
                          step="1"
                          value={item.quantity}
                          onChange={(e) =>
                            handleLaborItemChange(
                              index,
                              "quantity",
                              e.target.value,
                            )
                          }
                          className="w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                          disabled={
                            (isApproved &&
                              savedChangeOrder?.approvalStatus ===
                                "Approved") ||
                            (isRejected &&
                              savedChangeOrder?.approvalStatus === "Rejected")
                          }
                        />
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                        <input
                          type="text"
                          value={item.uom}
                          onChange={(e) =>
                            handleLaborItemChange(index, "uom", e.target.value)
                          }
                          className="w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                          disabled={
                            (isApproved &&
                              savedChangeOrder?.approvalStatus ===
                                "Approved") ||
                            (isRejected &&
                              savedChangeOrder?.approvalStatus === "Rejected")
                          }
                        />
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                        <Select
                          options={bidAreaOptions}
                          value={
                            bidAreaOptions.find(
                              (option) => option.value === item.bidArea,
                            ) || null
                          }
                          onChange={(option) =>
                            handleLaborItemChange(
                              index,
                              "bidArea",
                              option ? option.value : "",
                            )
                          }
                          placeholder="Select Bid Area"
                          styles={customStyles}
                          className="w-32"
                          isDisabled={
                            (isApproved &&
                              savedChangeOrder?.approvalStatus ===
                                "Approved") ||
                            (isRejected &&
                              savedChangeOrder?.approvalStatus === "Rejected")
                          }
                        />
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                        <input
                          type="number"
                          step="0.01"
                          value={item.unitCost}
                          onChange={(e) =>
                            handleLaborItemChange(
                              index,
                              "unitCost",
                              e.target.value,
                            )
                          }
                          className="w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                          disabled={
                            (isApproved &&
                              savedChangeOrder?.approvalStatus ===
                                "Approved") ||
                            (isRejected &&
                              savedChangeOrder?.approvalStatus === "Rejected")
                          }
                        />
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                        <input
                          type="number"
                          step="0.01"
                          value={item.unitPrice}
                          onChange={(e) =>
                            handleLaborItemChange(
                              index,
                              "unitPrice",
                              e.target.value,
                            )
                          }
                          className="w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                          disabled={
                            (isApproved &&
                              savedChangeOrder?.approvalStatus ===
                                "Approved") ||
                            (isRejected &&
                              savedChangeOrder?.approvalStatus === "Rejected")
                          }
                        />
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                        {formatNumber(item.quantity * item.unitCost)}
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                        {formatNumber(item.quantity * item.unitPrice)}
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap text-sm text-right">
                        <button
                          type="button"
                          onClick={() => handleDeleteLaborItem(index)}
                          className="text-red-400 hover:text-red-300 transition-colors duration-200"
                          disabled={
                            (isApproved &&
                              savedChangeOrder?.approvalStatus ===
                                "Approved") ||
                            (isRejected &&
                              savedChangeOrder?.approvalStatus === "Rejected")
                          }
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="bg-gray-800">
                  <td
                    colSpan="9"
                    className="px-6 py-3 text-right text-sm font-medium text-gray-200"
                  >
                    Total:
                  </td>
                  <td className="px-2 py-3 text-left text-sm font-medium text-gray-200">
                    {formatNumber(
                      laborItems
                        .filter((item) => item.budget)
                        .reduce(
                          (sum, item) => sum + item.quantity * item.unitCost,
                          0,
                        ),
                    )}
                  </td>
                  <td className="px-2 py-3 text-left text-sm font-medium text-gray-200">
                    {formatNumber(
                      laborItems
                        .filter((item) => item.customer)
                        .reduce(
                          (sum, item) => sum + item.quantity * item.unitPrice,
                          0,
                        ),
                    )}
                  </td>
                  <td className="px-2 py-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="bg-gray-700 p-6 rounded-lg border border-gray-600">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-white">Surcharges</h2>
              <button
                type="button"
                onClick={() => setShowSurchargeInput(!showSurchargeInput)}
                className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                disabled={
                  (isApproved &&
                    savedChangeOrder?.approvalStatus === "Approved") ||
                  (isRejected &&
                    savedChangeOrder?.approvalStatus === "Rejected")
                }
              >
                {showSurchargeInput ? "Hide Add Surcharge" : "Add Line Item"}
              </button>
            </div>
            {showSurchargeInput && (
              <div className="space-y-4">
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-200">
                      Title
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={newSurchargeItem.title}
                      onChange={handleSurchargeChange}
                      placeholder="Surcharge Title"
                      className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Cost Code
                    </label>
                    <Select
                      options={costCodeOptions}
                      value={
                        costCodeOptions.find(
                          (option) =>
                            option.value === newSurchargeItem.costCode,
                        ) || null
                      }
                      onChange={(option) =>
                        setNewSurchargeItem((prev) => ({
                          ...prev,
                          costCode: option ? option.value : "",
                        }))
                      }
                      placeholder="Select Cost Code"
                      styles={customStyles}
                      className="w-48 mt-1"
                      isDisabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Quantity
                    </label>
                    <input
                      type="number"
                      name="quantity"
                      value={newSurchargeItem.quantity}
                      onChange={handleSurchargeChange}
                      placeholder="Quantity"
                      step="1"
                      className="mt-1 w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Unit Cost
                    </label>
                    <input
                      type="number"
                      name="unitCost"
                      value={newSurchargeItem.unitCost}
                      onChange={handleSurchargeChange}
                      placeholder="Unit Cost"
                      step="0.01"
                      className="mt-1 w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Unit Price
                    </label>
                    <input
                      type="number"
                      name="unitPrice"
                      value={newSurchargeItem.unitPrice}
                      onChange={handleSurchargeChange}
                      placeholder="Unit Price"
                      step="0.01"
                      className="mt-1 w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Customer
                    </label>
                    <input
                      type="checkbox"
                      name="customer"
                      checked={newSurchargeItem.customer}
                      onChange={(e) =>
                        setNewSurchargeItem((prev) => ({
                          ...prev,
                          customer: e.target.checked,
                        }))
                      }
                      className="mt-1 h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Budget
                    </label>
                    <input
                      type="checkbox"
                      name="budget"
                      checked={newSurchargeItem.budget}
                      onChange={(e) =>
                        setNewSurchargeItem((prev) => ({
                          ...prev,
                          budget: e.target.checked,
                        }))
                      }
                      className="mt-1 h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={handleAddSurchargeItem}
                      className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                      disabled={
                        (isApproved &&
                          savedChangeOrder?.approvalStatus === "Approved") ||
                        (isRejected &&
                          savedChangeOrder?.approvalStatus === "Rejected")
                      }
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-4">
              <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Budget
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Cost Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Unit Cost
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Unit Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Total Cost
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-600">
                  {surchargeItems.length === 0 ? (
                    <tr>
                      <td
                        colSpan="9"
                        className="px-6 py-4 text-center text-sm text-gray-400"
                      >
                        No surcharge items added. Add items manually below or
                        upload a budget file.
                      </td>
                    </tr>
                  ) : (
                    surchargeItems.map((item, index) => (
                      <tr
                        key={item.uploadId || index}
                        className="hover:bg-gray-500 transition-colors duration-200"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                          <input
                            type="checkbox"
                            checked={item.customer}
                            onChange={(e) =>
                              handleSurchargeItemChange(
                                index,
                                "customer",
                                e.target.checked,
                              )
                            }
                            className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                            disabled={
                              (isApproved &&
                                savedChangeOrder?.approvalStatus ===
                                  "Approved") ||
                              (isRejected &&
                                savedChangeOrder?.approvalStatus === "Rejected")
                            }
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                          <input
                            type="checkbox"
                            checked={item.budget}
                            onChange={(e) =>
                              handleSurchargeItemChange(
                                index,
                                "budget",
                                e.target.checked,
                              )
                            }
                            className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                            disabled={
                              (isApproved &&
                                savedChangeOrder?.approvalStatus ===
                                  "Approved") ||
                              (isRejected &&
                                savedChangeOrder?.approvalStatus === "Rejected")
                            }
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                          <input
                            type="text"
                            value={item.title}
                            onChange={(e) =>
                              handleSurchargeItemChange(
                                index,
                                "title",
                                e.target.value,
                              )
                            }
                            className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                            disabled={
                              (isApproved &&
                                savedChangeOrder?.approvalStatus ===
                                  "Approved") ||
                              (isRejected &&
                                savedChangeOrder?.approvalStatus === "Rejected")
                            }
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                          <Select
                            options={costCodeOptions}
                            value={
                              costCodeOptions.find(
                                (option) => option.value === item.costCode,
                              ) || null
                            }
                            onChange={(option) =>
                              handleSurchargeItemChange(
                                index,
                                "costCode",
                                option ? option.value : null,
                              )
                            }
                            placeholder="Select Cost Code"
                            styles={customStyles}
                            className="w-48"
                            isDisabled={
                              (isApproved &&
                                savedChangeOrder?.approvalStatus ===
                                  "Approved") ||
                              (isRejected &&
                                savedChangeOrder?.approvalStatus === "Rejected")
                            }
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                          <input
                            type="number"
                            step="1"
                            value={item.quantity}
                            onChange={(e) =>
                              handleSurchargeItemChange(
                                index,
                                "quantity",
                                e.target.value,
                              )
                            }
                            className="w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                            disabled={
                              (isApproved &&
                                savedChangeOrder?.approvalStatus ===
                                  "Approved") ||
                              (isRejected &&
                                savedChangeOrder?.approvalStatus === "Rejected")
                            }
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                          <input
                            type="number"
                            step="0.01"
                            value={item.unitCost}
                            onChange={(e) =>
                              handleSurchargeItemChange(
                                index,
                                "unitCost",
                                e.target.value,
                              )
                            }
                            className="w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                            disabled={
                              (isApproved &&
                                savedChangeOrder?.approvalStatus ===
                                  "Approved") ||
                              (isRejected &&
                                savedChangeOrder?.approvalStatus === "Rejected")
                            }
                          />
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                          <input
                            type="number"
                            step="0.01"
                            value={item.unitPrice}
                            onChange={(e) =>
                              handleSurchargeItemChange(
                                index,
                                "unitPrice",
                                e.target.value,
                              )
                            }
                            className="w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                            disabled={
                              (isApproved &&
                                savedChangeOrder?.approvalStatus ===
                                  "Approved") ||
                              (isRejected &&
                                savedChangeOrder?.approvalStatus === "Rejected")
                            }
                          />
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                          {formatNumber(item.amount)}
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-right">
                          <button
                            type="button"
                            onClick={() => handleDeleteSurchargeItem(index)}
                            className="text-red-400 hover:text-red-300 transition-colors duration-200"
                            disabled={
                              (isApproved &&
                                savedChangeOrder?.approvalStatus ===
                                  "Approved") ||
                              (isRejected &&
                                savedChangeOrder?.approvalStatus === "Rejected")
                            }
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-800">
                    <td
                      colSpan="7"
                      className="px-6 py-3 text-right text-sm font-medium text-gray-200"
                    >
                      Total:
                    </td>
                    <td className="px-6 py-3 text-left text-sm font-medium text-gray-200">
                      {formatNumber(
                        surchargeItems
                          .filter((item) => item.budget)
                          .reduce(
                            (sum, item) => sum + item.quantity * item.unitCost,
                            0,
                          ),
                      )}
                    </td>
                    <td className="px-6 py-3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          <div className="bg-gray-700 p-6 rounded-lg border border-gray-600">
            <h2 className="text-lg font-semibold text-white mb-4">Summary</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
                <div className="md:col-span-6">
                  <label className="block text-sm font-medium text-gray-200">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={summary.description}
                    onChange={handleSummaryChange}
                    placeholder="Enter description"
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    rows="4"
                    disabled={
                      (isApproved &&
                        savedChangeOrder?.approvalStatus === "Approved") ||
                      (isRejected &&
                        savedChangeOrder?.approvalStatus === "Rejected")
                    }
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-200">
                    Qty/Unit
                  </label>
                  <input
                    type="text"
                    name="qtyUnit"
                    value={summary.qtyUnit}
                    onChange={handleSummaryChange}
                    placeholder="Qty/Unit"
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    disabled={
                      (isApproved &&
                        savedChangeOrder?.approvalStatus === "Approved") ||
                      (isRejected &&
                        savedChangeOrder?.approvalStatus === "Rejected")
                    }
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-200">
                    Revision
                  </label>
                  <input
                    type="number"
                    name="revision"
                    value={summary.revision}
                    onChange={handleSummaryChange}
                    placeholder="0"
                    step="1"
                    min="0"
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    disabled={
                      (isApproved &&
                        savedChangeOrder?.approvalStatus === "Approved") ||
                      (isRejected &&
                        savedChangeOrder?.approvalStatus === "Rejected")
                    }
                  />
                </div>
                <div className="md:col-span-2 flex items-center space-x-2">
                  <span className="text-sm text-gray-400">Billable</span>
                  <button
                    type="button"
                    onClick={handleBillableToggle}
                    className={`relative w-20 h-8 rounded-full flex items-center justify-between px-1 transition-colors ${
                      summary.billable ? "bg-orange-400" : "bg-gray-500"
                    }`}
                    disabled={
                      isRejected &&
                      savedChangeOrder?.approvalStatus === "Rejected"
                    }
                  >
                    <span className="w-1/2 text-center text-gray-200 text-sm z-10">
                      Yes
                    </span>
                    <span className="w-1/2 text-center text-gray-200 text-sm z-10">
                      No
                    </span>
                    <span
                      className={`absolute w-9 h-6 bg-gray-200 rounded-full top-1 transition-transform ${
                        summary.billable ? "translate-x-9" : "translate-x-0"
                      }`}
                    ></span>
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-x-2">
                <div className="md:col-span-4">
                  <label className="block text-sm font-medium text-gray-200">
                    Total Estimated Cost (Materials + Labor + Surcharges)
                  </label>
                  <input
                    type="text"
                    value={formatNumber(totalCost)}
                    disabled
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm sm:text-sm"
                  />
                </div>
                <div className="md:col-span-4">
                  <label className="block text-sm font-medium text-gray-200">
                    M&L+S Markup (%)
                  </label>
                  <input
                    type="text"
                    value={markup}
                    disabled
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm sm:text-sm"
                  />
                </div>
                <div className="md:col-span-4">
                  <label className="block text-sm font-medium text-gray-200">
                    Total Price for Customer (incl. OH)
                  </label>
                  <input
                    type="text"
                    value={formatNumber(totalPriceForCustomer)}
                    disabled
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm sm:text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() =>
                navigate(`/projects/${id}`, {
                  state: { initialTab: "Change Order (CO)" },
                })
              }
              className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`px-4 py-2 text-sm font-medium text-gray-200 rounded-md ${
                isLoading
                  ? "bg-gray-600 cursor-not-allowed opacity-50"
                  : "bg-orange-400 hover:bg-orange-500 transition-colors duration-200"
              }`}
            >
              Update Change Order
            </button>
            <button
              type="button"
              onClick={() => generateChangeOrderPDF(false)}
              disabled={!savedChangeOrder}
              className={`px-4 py-2 text-sm font-medium text-gray-200 rounded-md ${
                savedChangeOrder
                  ? "bg-blue-700 hover:bg-blue-900 transition-colors duration-200"
                  : "bg-gray-600 cursor-not-allowed opacity-50"
              }`}
            >
              Generate PDF
            </button>
            <button
              type="button"
              onClick={handleSendEmail}
              disabled={!savedChangeOrder}
              className={`px-4 py-2 text-sm font-medium text-gray-200 rounded-md ${
                savedChangeOrder
                  ? "bg-green-700 hover:bg-green-800 transition-colors duration-200"
                  : "bg-gray-600 cursor-not-allowed opacity-50"
              }`}
            >
              Send Email
            </button>
          </div>
        </form>
        <ConfirmationModal
          open={showConfirmationModal}
          onClose={handleCancelReject}
          onConfirm={handleConfirmReject}
        />
      </div>
    </div>
  );
}
